// React-based Blocks for GrapesJS
import type { GrapesJSEditor } from '../types/grapesjs';

export function registerBlocks(editor: GrapesJSEditor): void {
  const BlockManager = editor.BlockManager
  const DomComponents = editor.DomComponents

  // Define block categories
  const categories = {
    layout: 'Layout Elements',
    basic: 'Basic Elements', 
    typography: 'Typography',
    media: 'Media',
    components: 'Components',
    react: 'React Components',
    heroSections: '🏠 Hero Sections',
    propertyDisplays: '🏘️ Property Displays',
    searchFilters: '🔍 Search & Filters',
    maps: '🗺️ Maps',
    aboutTeam: '👤 About & Team',
    formsContact: '📧 Forms & Contact'
  }

  // Register custom component type for React components
  DomComponents.addType('hero-with-search', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'hero-with-search-container' },
        content: `
          <style>
            .hero-with-search { position: relative; min-height: 70vh; width: 100%; display: flex; align-items: center; justify-content: center; overflow: hidden; }
            .hero-bg { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-image: url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1600&q=80&auto=format&fit=crop'); background-size: cover; background-position: center; filter: brightness(0.7) blur(0px); z-index: 1; }
            .hero-overlay { position: relative; z-index: 2; width: 100%; display: flex; align-items: center; justify-content: center; min-height: 70vh; background: linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 100%); }
            .hero-content { text-align: center; color: #fff; max-width: 700px; margin: 0 auto; padding: 48px 24px; border-radius: 24px; background: rgba(0,0,0,0.25); box-shadow: 0 8px 32px rgba(0,0,0,0.18); }
            .hero-title { font-size: 3rem; font-weight: 700; margin-bottom: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; letter-spacing: -1px; }
            .hero-subtitle { font-size: 1.25rem; font-weight: 400; margin-bottom: 32px; color: #e5e5e7; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .hero-search-form { display: flex; gap: 16px; justify-content: center; align-items: center; background: rgba(255,255,255,0.12); border-radius: 16px; padding: 12px 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); flex-wrap: wrap; }
            .hero-search-input, .hero-search-select { padding: 14px 18px; border: none; border-radius: 10px; font-size: 1rem; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: rgba(255,255,255,0.95); color: #1D1D1F; min-width: 180px; margin: 4px 0; outline: none; transition: box-shadow 0.2s; }
            .hero-search-input:focus, .hero-search-select:focus { box-shadow: 0 0 0 3px rgba(0,122,255,0.18); }
            .hero-search-btn { display: flex; align-items: center; gap: 8px; background: #007AFF; color: #fff; border: none; border-radius: 10px; padding: 14px 28px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: background 0.2s, transform 0.2s; margin: 4px 0; }
            .hero-search-btn:hover:not(:disabled) { background: #0056CC; transform: translateY(-2px); }
            .hero-search-btn:disabled { background: #999; cursor: not-allowed; transform: none; }
            .loading-spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid #fff; border-radius: 50%; animation: spin 1s linear infinite; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .search-results { padding: 48px 24px; background: #f8f9fa; min-height: 400px; }
            .results-header { text-align: center; margin-bottom: 32px; }
            .results-header h2 { font-size: 2rem; font-weight: 600; color: #1D1D1F; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .results-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; max-width: 1200px; margin: 0 auto; }
            .property-card { background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: transform 0.2s, box-shadow 0.2s; }
            .property-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
            .property-image { height: 200px; overflow: hidden; position: relative; }
            .property-image img { width: 100%; height: 100%; object-fit: cover; }
            .no-image { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f0f0f0; color: #666; font-size: 0.9rem; }
            .property-details { padding: 20px; }
            .property-address { font-size: 1.1rem; font-weight: 600; color: #1D1D1F; margin: 0 0 8px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .property-price { font-size: 1.2rem; font-weight: 700; color: #007AFF; margin: 0 0 12px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .property-features { display: flex; gap: 16px; font-size: 0.9rem; color: #6E6E73; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            @media (max-width: 768px) { .hero-content { padding: 32px 8px; } .hero-title { font-size: 2rem; } .hero-search-form { flex-direction: column; gap: 10px; padding: 10px 6px; } .hero-search-input, .hero-search-select { min-width: 100%; width: 100%; } .hero-search-btn { width: 100%; justify-content: center; } .results-grid { grid-template-columns: 1fr; gap: 16px; } }
          </style>
          <div class="hero-with-search">
            <div class="hero-bg"></div>
            <div class="hero-overlay">
              <div class="hero-content">
                <h1 class="hero-title">Find Your Dream Home</h1>
                <p class="hero-subtitle">Luxury properties, modern living, and the best locations—all in one place.</p>
                <div class="hero-search-form">
                  <input type="text" class="hero-search-input" placeholder="City, neighborhood, or ZIP code" name="location" />
                  <select class="hero-search-select" name="propertyType">
                    <option value="">Any Type</option>
                    <option value="house">House</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="apartment">Apartment</option>
                    <option value="land">Land</option>
                  </select>
                  <button type="button" class="hero-search-btn" id="search-properties-btn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                    </svg>
                    Search Properties
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="search-results" id="search-results" style="display: none;">
            <div class="results-header">
              <h2>Search Results</h2>
            </div>
            <div class="results-grid" id="results-grid"></div>
          </div>
          
          <!-- Property Detail Modal -->
          <div id="property-detail-modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); z-index: 1000; overflow-y: auto;">
            <div style="background: white; margin: 20px auto; max-width: 1000px; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.3);">
              <div id="property-detail-content"></div>
            </div>
          </div>
        `,
        script: function() {
          const searchBtn = document.getElementById('search-properties-btn');
          const searchResults = document.getElementById('search-results');
          const resultsGrid = document.getElementById('results-grid');
          const locationInput = document.querySelector('.hero-search-input');
          const propertyTypeSelect = document.querySelector('.hero-search-select');

          if (searchBtn) {
            searchBtn.addEventListener('click', async function(e) {
              e.preventDefault();
              e.stopPropagation();
              
              const location = locationInput ? locationInput.value : '';
              const propertyType = propertyTypeSelect ? propertyTypeSelect.value : '';
              
              // Show loading state
              searchBtn.innerHTML = '<div class="loading-spinner"></div>Searching...';
              searchBtn.disabled = true;
              
              try {
                // Goes through the backend proxy instead of calling Repliers
                // directly from the browser — a direct call would require
                // shipping a live Repliers API key in frontend source, which
                // every visitor's browser would then have access to.
                const endpoint = location
                  ? `https://api.godreamx.com/api/repliers/search?q=${encodeURIComponent(location)}&format=raw`
                  : `https://api.godreamx.com/api/repliers/listings?sortBy=updatedOnDesc&format=raw`;
                const response = await fetch(endpoint);

                if (!response.ok) {
                  throw new Error(`API request failed: ${response.status}`);
                }

                const data = await response.json();
                console.log('API Response:', data);
                
                // Handle different response structures
                let listings = [];
                if (Array.isArray(data)) {
                  listings = data;
                } else if (data.listings && Array.isArray(data.listings)) {
                  listings = data.listings;
                } else if (data.data && Array.isArray(data.data)) {
                  listings = data.data;
                }
                
                // Display results
                if (listings.length > 0) {
                  resultsGrid.innerHTML = listings.map((property, index) => {
                    // Extract property data with proper field mapping based on actual API response
                    const address = property.address ? 
                      `${property.address.streetNumber || ''} ${property.address.streetName || ''} ${property.address.streetSuffix || ''}, ${property.address.city || ''}, ${property.address.state || ''}` : 
                      'Address not available';
                    
                    const price = property.listPrice ? 
                      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(property.listPrice)) : 
                      'Price not available';
                    
                    const bedrooms = property.details?.numBedrooms || 'N/A';
                    const bathrooms = property.details?.numBathrooms || 'N/A';
                    const sqft = property.details?.sqft || 'N/A';
                    
                    // Get first image or use placeholder
                    // The API returns relative paths like "sandbox/IMG-SANDBOX_1.jpg"
                    // For now, we'll use a placeholder since the actual image URLs require additional API calls
                    // TODO: Find correct image endpoint for Repliers API
                    const imageUrl = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&q=80';
                    const photoCount = property.photoCount || 0;
                    
                    return `
                      <div class="property-card" data-property-index="${index}" style="cursor: pointer;">
                        <div class="property-image" style="background-image: url('${imageUrl}'); background-size: cover; background-position: center; height: 200px; position: relative;">
                          <div style="position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.8); color: white; padding: 5px 10px; border-radius: 15px; font-weight: bold;">
                            ${price}
                          </div>
                          ${photoCount > 0 ? `<div style="position: absolute; top: 10px; left: 10px; background: rgba(0,0,0,0.8); color: white; padding: 5px 10px; border-radius: 15px; font-size: 12px; font-weight: bold;">
                            📸 ${photoCount} photos
                          </div>` : ''}
                        </div>
                        <div class="property-details" style="padding: 15px;">
                          <h3 class="property-address" style="margin: 0 0 10px 0; font-size: 16px; font-weight: bold;">${address}</h3>
                          <div class="property-features" style="display: flex; gap: 15px; color: #666; font-size: 14px;">
                            <span>${bedrooms} Beds</span>
                            <span>${bathrooms} Baths</span>
                            <span>${sqft} sqft</span>
                          </div>
                        </div>
                      </div>
                    `;
                  }).join('');
                  
                  searchResults.style.display = 'block';
                  
                  // Add click handlers to property cards. Look the property
                  // up from the same `listings` array already in closure
                  // scope (by the index each card was rendered with) rather
                  // than round-tripping it through a data-* attribute --
                  // JSON.stringify(property) embedded in a single-quoted
                  // HTML attribute breaks (silently truncates mid-string)
                  // whenever any field contains an apostrophe, which real
                  // address/remarks/agent-name data does often enough that
                  // this was failing on live listings, not just a
                  // theoretical edge case.
                  const propertyCards = resultsGrid.querySelectorAll('.property-card');
                  propertyCards.forEach(card => {
                    card.addEventListener('click', function() {
                      const index = parseInt(this.getAttribute('data-property-index'), 10);
                      const propertyData = listings[index];
                      if (propertyData) showPropertyDetail(propertyData);
                    });
                  });
                } else {
                  resultsGrid.innerHTML = '<p>No properties found.</p>';
                  searchResults.style.display = 'block';
                }
              } catch (err) {
                console.error('Search error:', err);
                resultsGrid.innerHTML = '<p>Failed to fetch listings. Please try again.</p>';
                searchResults.style.display = 'block';
              } finally {
                // Reset button
                searchBtn.innerHTML = `
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                  </svg>
                  Search Properties
                `;
                searchBtn.disabled = false;
              }
            });
          }
          
          // Function to show property detail modal
          function showPropertyDetail(property) {
            const modal = document.getElementById('property-detail-modal');
            const content = document.getElementById('property-detail-content');
            
            // Format property data
            const address = property.address ? 
              `${property.address.streetNumber || ''} ${property.address.streetName || ''} ${property.address.streetSuffix || ''}, ${property.address.city || ''}, ${property.address.state || ''} ${property.address.zipCode || ''}` : 
              'Address not available';
            
            const price = property.listPrice ? 
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(property.listPrice)) : 
              'Price not available';
            
            const bedrooms = property.details?.numBedrooms || 'N/A';
            const bathrooms = property.details?.numBathrooms || 'N/A';
            const sqft = property.details?.sqft ? new Intl.NumberFormat('en-US').format(property.details.sqft) : 'N/A';
            const lotSize = property.details?.lotSize ? new Intl.NumberFormat('en-US').format(property.details.lotSize) : 'N/A';
            const yearBuilt = property.details?.yearBuilt || 'N/A';
            const propertyType = property.details?.propertyType || 'N/A';
            const mlsNumber = property.mlsNumber || 'N/A';
            const photoCount = property.photoCount || 0;
            const status = property.status || 'N/A';
            
            // Create detailed property view
            content.innerHTML = `
              <div style="position: relative;">
                <!-- Header with close button -->
                <div style="position: absolute; top: 20px; right: 20px; z-index: 10;">
                  <button onclick="document.getElementById('property-detail-modal').style.display='none'" style="background: rgba(0,0,0,0.7); color: white; border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; font-size: 20px;">×</button>
                </div>
                
                <!-- Hero Image -->
                <div style="height: 400px; background-image: url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80'); background-size: cover; background-position: center; position: relative;">
                  <div style="position: absolute; bottom: 0; left: 0; right: 0; background: linear-gradient(transparent, rgba(0,0,0,0.8)); padding: 40px 30px 30px;">
                    <h1 style="color: white; margin: 0; font-size: 2.5rem; font-weight: bold;">${price}</h1>
                    <p style="color: white; margin: 10px 0 0 0; font-size: 1.2rem; opacity: 0.9;">${address}</p>
                  </div>
                </div>
                
                <!-- Property Details -->
                <div style="padding: 40px 30px;">
                  <!-- Key Stats -->
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 30px; margin-bottom: 40px; padding: 30px; background: #f8f9fa; border-radius: 16px;">
                    <div style="text-align: center;">
                      <div style="font-size: 2rem; font-weight: bold; color: #007AFF;">${bedrooms}</div>
                      <div style="color: #666; font-size: 0.9rem;">Bedrooms</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 2rem; font-weight: bold; color: #007AFF;">${bathrooms}</div>
                      <div style="color: #666; font-size: 0.9rem;">Bathrooms</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 2rem; font-weight: bold; color: #007AFF;">${sqft}</div>
                      <div style="color: #666; font-size: 0.9rem;">Square Feet</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 2rem; font-weight: bold; color: #007AFF;">${photoCount}</div>
                      <div style="color: #666; font-size: 0.9rem;">Photos</div>
                    </div>
                  </div>
                  
                  <!-- Detailed Information -->
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px;">
                    <div>
                      <h3 style="margin: 0 0 20px 0; color: #333; font-size: 1.5rem;">Property Information</h3>
                      <div style="display: grid; gap: 15px;">
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">Property Type:</span>
                          <span>${propertyType}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">Status:</span>
                          <span style="color: ${status === 'A' ? '#28a745' : '#dc3545'}">${status === 'A' ? 'Active' : status}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">MLS Number:</span>
                          <span>${mlsNumber}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">Year Built:</span>
                          <span>${yearBuilt}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">Lot Size:</span>
                          <span>${lotSize} sqft</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 style="margin: 0 0 20px 0; color: #333; font-size: 1.5rem;">Location Details</h3>
                      <div style="display: grid; gap: 15px;">
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">Street:</span>
                          <span>${property.address?.streetNumber || ''} ${property.address?.streetName || ''} ${property.address?.streetSuffix || ''}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">City:</span>
                          <span>${property.address?.city || 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">State:</span>
                          <span>${property.address?.state || 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">ZIP Code:</span>
                          <span>${property.address?.zipCode || 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
                          <span style="font-weight: 500;">County:</span>
                          <span>${property.address?.county || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Action Buttons -->
                  <div style="margin-top: 40px; display: flex; gap: 15px; justify-content: center;">
                    <button class="modal-contact-btn" style="padding: 15px 30px; background: #007AFF; color: white; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer;">Contact Agent</button>
                    <button class="modal-schedule-btn" style="padding: 15px 30px; background: #28a745; color: white; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer;">Schedule Viewing</button>
                    <button class="modal-save-btn" style="padding: 15px 30px; background: #6c757d; color: white; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer;">Save Property</button>
                  </div>
                </div>
              </div>
            `;

            modal.style.display = 'block';

            // These three were previously bare <button>s with no class, no
            // handler, and no href -- clicking any of them did nothing.
            var modalAddress = ((property.address?.streetNumber || '') + ' ' + (property.address?.streetName || '') + ' ' + (property.address?.streetSuffix || '')).trim();

            var contactBtn = content.querySelector('.modal-contact-btn');
            if (contactBtn) {
              contactBtn.addEventListener('click', function () {
                modal.style.display = 'none';
                var form = document.querySelector('.agent-form, form');
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  var msgField = form.querySelector('[name="message"]');
                  if (msgField && !msgField.value) msgField.value = 'I\'m interested in ' + (modalAddress || 'this property') + '.';
                  var firstInput = form.querySelector('input, textarea, select');
                  if (firstInput) firstInput.focus();
                }
              });
            }

            var scheduleBtn = content.querySelector('.modal-schedule-btn');
            if (scheduleBtn) {
              scheduleBtn.addEventListener('click', function () {
                modal.style.display = 'none';
                var form = document.querySelector('.agent-form, form');
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  var msgField = form.querySelector('[name="message"]');
                  if (msgField) msgField.value = 'I\'d like to schedule a viewing for ' + (modalAddress || 'this property') + '.';
                  var firstInput = form.querySelector('input, textarea, select');
                  if (firstInput) firstInput.focus();
                }
              });
            }

            var saveBtn = content.querySelector('.modal-save-btn');
            if (saveBtn) {
              saveBtn.addEventListener('click', function () {
                try {
                  var saved = JSON.parse(window.localStorage.getItem('gdxSavedProperties') || '[]');
                  var key = property.mlsNumber || modalAddress;
                  if (!saved.includes(key)) {
                    saved.push(key);
                    window.localStorage.setItem('gdxSavedProperties', JSON.stringify(saved));
                  }
                  saveBtn.textContent = 'Saved ✓';
                  saveBtn.disabled = true;
                } catch (e) {
                  saveBtn.textContent = 'Save Property';
                }
              });
            }

            // Close modal when clicking outside
            modal.addEventListener('click', function(e) {
              if (e.target === modal) {
                modal.style.display = 'none';
              }
            });
          }
        }
      }
    }
  });

  // Get block content function (defined later when blocks are registered)
  // This will be set after blocks are registered
  let getBlockContentById = (id: string): string => {
    const block = BlockManager.get(id);
    return block ? block.get('content') as string : '<div>Content not found</div>';
  };

  // Register additional component types for templates
  DomComponents.addType('real-estate-property-listing-page', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .property-listing-page { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1d1d1f; background: #f5f5f7; min-height: 100vh; }
            .listing-header { background: linear-gradient(135deg, #007aff 0%, #5856d6 100%); color: white; padding: 60px 0; }
            .header-content { display: flex; justify-content: space-between; align-items: center; }
            .header-left h1 { font-size: 3rem; font-weight: 700; margin: 0 0 10px 0; line-height: 1.1; }
            .header-left p { font-size: 1.2rem; margin: 0; opacity: 0.9; }
            .view-controls { display: flex; gap: 8px; background: rgba(255,255,255,0.1); border-radius: 12px; padding: 4px; backdrop-filter: blur(10px); }
            .view-btn { background: none; border: none; color: white; padding: 8px 12px; border-radius: 8px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; }
            .view-btn.active { background: rgba(255,255,255,0.2); backdrop-filter: blur(10px); }
            .listing-controls { background: white; border-bottom: 1px solid #e5e5e7; padding: 20px 0; }
            .controls-row { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
            .filter-toggle { display: flex; align-items: center; gap: 8px; background: #f5f5f7; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; font-weight: 500; transition: all 0.3s ease; }
            .filter-toggle.active { background: #007aff; color: white; }
            .results-count { color: #86868b; font-size: 0.95rem; margin-left: 12px; }
            .sort-select { padding: 10px 16px; border: 1px solid #e5e5e7; border-radius: 8px; background: white; font-size: 0.95rem; cursor: pointer; }
            .property-results { padding: 40px 0; }
            .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
            @media (max-width: 768px) { .header-content { flex-direction: column; gap: 20px; text-align: center; } }
          </style>
          <div class="property-listing-page">
            <section class="listing-header">
              <div class="container">
                <div class="header-content">
                  <div class="header-left">
                    <h1>Properties for Sale</h1>
                    <p>Discover your dream home in the most desirable locations</p>
                  </div>
                  <div class="header-right">
                    <div class="view-controls">
                      <button type="button" class="view-btn active">Grid</button>
                      <button type="button" class="view-btn">List</button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section class="listing-controls">
              <div class="container">
                <div class="controls-row">
                  <div class="controls-left">
                    <button type="button" class="filter-toggle">Filters</button>
                    <span class="results-count">Properties</span>
                  </div>
                  <div class="controls-right">
                    <select class="sort-select">
                      <option>Price: High to Low</option>
                      <option>Price: Low to High</option>
                      <option>Most Bedrooms</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>
            <section class="property-results">
              <div class="container">
                <div class="gdx-properties" data-type="grid" data-columns="3" data-limit="6"></div>
              </div>
            </section>
          </div>
        `,
        script: function () {
          const root = this;
          const container = root.querySelector('.gdx-properties');
          if (!container || container.dataset.gdxWired) return;
          container.dataset.gdxWired = 'true';

          const viewBtns = root.querySelectorAll('.view-btn');
          const gridBtn = viewBtns[0];
          const listBtn = viewBtns[1];
          const filterToggleBtn = root.querySelector('.filter-toggle');
          const controlsRow = root.querySelector('.controls-row');

          function reinit() {
            if (window.GDXProperties && window.GDXProperties.init) {
              window.GDXProperties.init();
            }
          }

          if (gridBtn && listBtn) {
            gridBtn.addEventListener('click', function () {
              gridBtn.classList.add('active');
              listBtn.classList.remove('active');
              container.dataset.columns = '3';
              reinit();
            });
            listBtn.addEventListener('click', function () {
              listBtn.classList.add('active');
              gridBtn.classList.remove('active');
              container.dataset.columns = '1';
              reinit();
            });
          }

          if (filterToggleBtn && controlsRow) {
            let panel = null;
            filterToggleBtn.addEventListener('click', function () {
              if (panel) {
                panel.remove();
                panel = null;
                filterToggleBtn.classList.remove('active');
                return;
              }
              filterToggleBtn.classList.add('active');
              panel = document.createElement('div');
              panel.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;padding:16px 0 0 0;width:100%;';
              panel.innerHTML =
                '<input type="number" placeholder="Min Price" class="gdx-filter-min-price" style="padding:10px 14px;border:1px solid #e5e5e7;border-radius:8px;min-width:120px;">' +
                '<input type="number" placeholder="Max Price" class="gdx-filter-max-price" style="padding:10px 14px;border:1px solid #e5e5e7;border-radius:8px;min-width:120px;">' +
                '<input type="number" placeholder="Min Beds" class="gdx-filter-beds" style="padding:10px 14px;border:1px solid #e5e5e7;border-radius:8px;min-width:100px;">' +
                '<button type="button" class="gdx-filter-apply" style="padding:10px 20px;border:none;border-radius:8px;background:#007aff;color:#fff;font-weight:600;cursor:pointer;">Apply Filters</button>';
              controlsRow.appendChild(panel);

              panel.querySelector('.gdx-filter-apply').addEventListener('click', function () {
                const minPrice = panel.querySelector('.gdx-filter-min-price').value;
                const maxPrice = panel.querySelector('.gdx-filter-max-price').value;
                const beds = panel.querySelector('.gdx-filter-beds').value;
                if (minPrice) container.dataset.minPrice = minPrice; else delete container.dataset.minPrice;
                if (maxPrice) container.dataset.maxPrice = maxPrice; else delete container.dataset.maxPrice;
                if (beds) container.dataset.bedrooms = beds; else delete container.dataset.bedrooms;
                reinit();
              });
            });
          }
        }
      }
    }
  });

  DomComponents.addType('real-estate-agent-profile', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .agent-profile { background: white; padding: 80px 0; border-radius: 0; }
            .agent-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
            .agent-header { display: grid; grid-template-columns: auto 1fr; gap: 48px; align-items: start; margin-bottom: 64px; }
            .agent-image-container { position: relative; }
            .agent-image { width: 200px; height: 200px; border-radius: 50%; object-fit: cover; border: 4px solid #f2f2f2; transition: all 0.3s ease; }
            .agent-image:hover { border-color: #007AFF; transform: scale(1.02); }
            .agent-status { position: absolute; bottom: 8px; right: 8px; background: #34C759; color: white; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3); }
            .status-dot { width: 8px; height: 8px; background: white; border-radius: 50%; animation: pulse 2s infinite; }
            @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
            .agent-info { display: flex; flex-direction: column; gap: 16px; }
            .agent-name { font-size: 48px; font-weight: 700; color: #1D1D1F; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; letter-spacing: -0.5px; }
            .agent-title { font-size: 20px; color: #6E6E73; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .agent-experience { display: flex; align-items: center; gap: 8px; font-size: 16px; color: #007AFF; font-weight: 600; }
            .agent-contact { display: flex; gap: 24px; margin-top: 8px; }
            .contact-item { display: flex; align-items: center; gap: 8px; color: #1D1D1F; text-decoration: none; font-size: 16px; font-weight: 500; transition: color 0.2s ease; }
            .contact-item:hover { color: #007AFF; }
            .contact-item svg { color: #007AFF; }
            .agent-content { display: grid; grid-template-columns: 2fr 1fr; gap: 64px; }
            .agent-bio h3, .agent-specialties h3, .agent-achievements h3 { font-size: 24px; font-weight: 600; color: #1D1D1F; margin: 0 0 16px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .agent-bio p { font-size: 18px; line-height: 1.6; color: #424245; margin: 0 0 32px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .specialties-list { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 32px; }
            .specialty-tag { background: #f2f2f2; color: #1D1D1F; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 500; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; transition: all 0.2s ease; }
            .specialty-tag:hover { background: #007AFF; color: white; transform: translateY(-1px); }
            .achievements-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
            .achievement-item { display: flex; align-items: center; gap: 12px; font-size: 16px; color: #424245; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .achievement-item svg { color: #34C759; flex-shrink: 0; }
            .agent-details { background: #f5f5f7; padding: 24px; border-radius: 16px; margin-bottom: 32px; }
            .detail-row { display: flex; gap: 12px; margin-bottom: 8px; font-size: 15px; color: #6E6E73; }
            .detail-label { font-weight: 600; }
            .detail-value { font-weight: 400; }
            .agent-actions { display: flex; gap: 24px; margin-top: 24px; }
            .contact-agent-btn, .schedule-btn { padding: 16px 32px; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s ease; display: flex; align-items: center; gap: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .contact-agent-btn.primary { background: #007AFF; color: white; }
            .contact-agent-btn.primary:hover { background: #0056CC; }
            .schedule-btn.secondary { background: #f2f2f2; color: #1D1D1F; }
            .schedule-btn.secondary:hover { background: #007AFF; color: white; }
            @media (max-width: 768px) { .agent-profile { padding: 40px 0; } .agent-container { padding: 0 16px; } .agent-header { grid-template-columns: 1fr; gap: 24px; margin-bottom: 32px; } .agent-image { width: 120px; height: 120px; } .agent-name { font-size: 32px; } .agent-title { font-size: 16px; } .agent-contact { gap: 12px; } .agent-content { grid-template-columns: 1fr; gap: 24px; } .agent-bio p { font-size: 15px; } .agent-actions { flex-direction: column; gap: 12px; } }
          </style>
          <div class="agent-profile">
            <div class="agent-container">
              <div class="agent-header">
                <div class="agent-image-container">
                  <img src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face" alt="Sarah Johnson" class="agent-image" />
                  <div class="agent-status"><div class="status-dot"></div><span>Available</span></div>
                </div>
                <div class="agent-info">
                  <h2 class="agent-name">Sarah Johnson</h2>
                  <p class="agent-title">Senior Real Estate Agent</p>
                  <div class="agent-experience"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>15+ years of Experience</div>
                  <div class="agent-contact">
                    <a href="tel:+13105550123" class="contact-item"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/></svg>+1 (310) 555-0123</a>
                    <a href="mailto:sarah.johnson@dreamxrealty.com" class="contact-item"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/></svg>sarah.johnson@dreamxrealty.com</a>
                  </div>
                </div>
              </div>
              <div class="agent-content">
                <div class="agent-bio"><h3>About Sarah</h3><p>Sarah is a top-performing real estate agent with over 15 years of experience in the luxury market. She specializes in helping clients find their dream homes in the most prestigious neighborhoods of Los Angeles.</p></div>
                <div class="agent-specialties"><h3>Specialties</h3><div class="specialties-list"><span class="specialty-tag">Luxury Homes</span><span class="specialty-tag">Investment Properties</span><span class="specialty-tag">First-Time Buyers</span></div></div>
                <div class="agent-achievements"><h3>Achievements</h3><div class="achievements-list"><div class="achievement-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>Top 1% of Agents Nationwide</div><div class="achievement-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>Certified Luxury Home Specialist</div><div class="achievement-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>Million Dollar Club Member</div></div></div>
                <div class="agent-details"><div class="detail-row"><span class="detail-label">Languages:</span><span class="detail-value">English, Spanish</span></div><div class="detail-row"><span class="detail-label">License:</span><span class="detail-value">CA DRE #01234567</span></div></div>
                <div class="agent-actions"><button class="contact-agent-btn primary"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-2 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/></svg>Contact Agent</button><button class="schedule-btn secondary"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/></svg>Schedule Meeting</button></div>
              </div>
            </div>
          </div>
        `,
        script: function () {
          const root = this;
          const contactBtn = root.querySelector('.contact-agent-btn');
          const scheduleBtn = root.querySelector('.schedule-btn');
          const emailLink = root.querySelector('.contact-item[href^="mailto:"]');
          const email = emailLink ? emailLink.getAttribute('href').replace('mailto:', '') : '';

          function focusNearestForm() {
            const form = document.querySelector('.agent-form');
            if (form) {
              form.scrollIntoView({ behavior: 'smooth', block: 'center' });
              const firstInput = form.querySelector('input, textarea');
              if (firstInput) firstInput.focus();
              return true;
            }
            return false;
          }

          if (contactBtn && !contactBtn.dataset.gdxWired) {
            contactBtn.dataset.gdxWired = 'true';
            contactBtn.addEventListener('click', function () {
              if (!focusNearestForm() && email) {
                window.location.href = 'mailto:' + email;
              }
            });
          }

          if (scheduleBtn && !scheduleBtn.dataset.gdxWired) {
            scheduleBtn.dataset.gdxWired = 'true';
            scheduleBtn.addEventListener('click', function () {
              if (email) {
                window.location.href = 'mailto:' + email + '?subject=' + encodeURIComponent('Schedule a Meeting');
              } else {
                focusNearestForm();
              }
            });
          }
        }
      }
    }
  });

  DomComponents.addType('real-estate-contact-agent-form', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' }
      },
      initialize() {
        // When component is instantiated, get the actual block content
        const blockContent = getBlockContentById('real-estate-contact-agent-form');
        this.set('content', blockContent);
      }
    }
  });

  DomComponents.addType('real-estate-featured-properties-carousel', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' }
      },
      initialize() {
        const blockContent = getBlockContentById('real-estate-featured-properties-carousel');
        this.set('content', blockContent);
      }
    }
  });

  DomComponents.addType('real-estate-testimonials-section', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `<style>.testimonials-section { background: #f5f5f7; padding: 80px 0; border-radius: 0; } .testimonials-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; } .testimonials-header { text-align: center; margin-bottom: 64px; } .testimonials-title { font-size: 48px; font-weight: 700; color: #1D1D1F; margin: 0 0 16px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; letter-spacing: -0.5px; } .testimonials-subtitle { font-size: 20px; color: #6E6E73; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; } .testimonials-content { display: flex; align-items: center; gap: 32px; margin-bottom: 48px; } .nav-button { background: white; border: 2px solid #f2f2f2; border-radius: 50%; width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; color: #6E6E73; flex-shrink: 0; } .nav-button:hover { border-color: #007AFF; color: #007AFF; transform: scale(1.05); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); } .nav-button svg { width: 24px; height: 24px; } .testimonial-card { background: white; border-radius: 24px; padding: 48px; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08); border: 1px solid #f2f2f2; flex: 1; max-width: 800px; margin: 0 auto; } .testimonial-header { display: flex; align-items: center; gap: 24px; margin-bottom: 32px; } .testimonial-image { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; border: 3px solid #f2f2f2; } .testimonial-info { flex: 1; } .testimonial-name { font-size: 24px; font-weight: 600; color: #1D1D1F; margin: 0 0 4px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; } .testimonial-role { font-size: 16px; color: #007AFF; font-weight: 500; margin: 0 0 4px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; } .testimonial-location { font-size: 14px; color: #6E6E73; margin: 0 0 12px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; } .testimonial-rating { display: flex; gap: 4px; } .star-icon { width: 20px; height: 20px; } .testimonial-text { font-size: 20px; line-height: 1.6; color: #424245; margin: 0 0 24px 0; font-style: italic; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; position: relative; } .testimonial-text::before { content: '"'; font-size: 48px; color: #007AFF; position: absolute; top: -20px; left: -20px; font-family: serif; } .testimonial-property { display: flex; align-items: center; gap: 8px; font-size: 16px; color: #007AFF; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; } .testimonial-property svg { color: #007AFF; } .testimonial-indicators { display: flex; justify-content: center; gap: 12px; margin-bottom: 64px; } .indicator { width: 12px; height: 12px; border-radius: 50%; background: #e5e5e7; border: none; cursor: pointer; transition: all 0.2s ease; } .indicator.active { background: #007AFF; transform: scale(1.2); } .indicator:hover { background: #007AFF; transform: scale(1.1); } .testimonials-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 48px; text-align: center; } .stat-item { display: flex; flex-direction: column; align-items: center; gap: 8px; } .stat-number { font-size: 32px; font-weight: 700; color: #007AFF; } .stat-label { font-size: 16px; color: #6E6E73; } @media (max-width: 768px) { .testimonials-section { padding: 40px 0; } .testimonials-container { padding: 0 16px; } .testimonials-title { font-size: 32px; } .testimonials-subtitle { font-size: 16px; } .testimonials-header { margin-bottom: 32px; } .testimonials-content { flex-direction: column; gap: 16px; margin-bottom: 24px; } .nav-button { width: 40px; height: 40px; } .testimonial-card { padding: 24px; } .testimonial-header { gap: 12px; margin-bottom: 16px; } .testimonial-image { width: 48px; height: 48px; } .testimonial-name { font-size: 18px; } .testimonial-text { font-size: 15px; } .testimonials-stats { grid-template-columns: 1fr; gap: 24px; } .stat-number { font-size: 20px; } }
</style><div class="testimonials-section"><div class="testimonials-container"><div class="testimonials-header"><h2 class="testimonials-title">What Our Clients Say</h2><p class="testimonials-subtitle">Real stories from satisfied clients</p></div><div class="testimonials-content"><button class="nav-button prev" aria-label="Previous testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/></svg></button><div class="testimonial-card"></div><button class="nav-button next" aria-label="Next testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor"/></svg></button></div><div class="testimonial-indicators"><button class="indicator active" aria-label="Go to testimonial 1"></button><button class="indicator" aria-label="Go to testimonial 2"></button><button class="indicator" aria-label="Go to testimonial 3"></button></div><div class="testimonials-stats"><div class="stat-item"><div class="stat-number">500+</div><div class="stat-label">Happy Clients</div></div><div class="stat-item"><div class="stat-number">$2.5B+</div><div class="stat-label">Properties Sold</div></div><div class="stat-item"><div class="stat-number">4.9/5</div><div class="stat-label">Average Rating</div></div></div></div></div>`,
        script: function () {
          const root = this;
          const card = root.querySelector('.testimonial-card');
          const indicators = root.querySelectorAll('.indicator');
          const prevBtn = root.querySelector('.nav-button.prev');
          const nextBtn = root.querySelector('.nav-button.next');
          if (!card || card.dataset.gdxWired) return;
          card.dataset.gdxWired = 'true';

          const TESTIMONIALS = [
            {
              name: 'Michael Rodriguez', role: 'Home Buyer', location: 'Beverly Hills, CA',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
              text: 'Sarah helped us find our dream home in Beverly Hills. Her expertise in the luxury market is unmatched. She made the entire process smooth and enjoyable.',
              property: 'Luxury Villa - $2.8M'
            },
            {
              name: 'Jennifer Chen', role: 'Home Seller', location: 'Malibu, CA',
              image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
              text: 'We sold our beach house above asking price within two weeks. The marketing and negotiation strategy were exceptional from start to finish.',
              property: 'Oceanfront Estate - $4.1M'
            },
            {
              name: 'David Thompson', role: 'Investor', location: 'Santa Monica, CA',
              image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
              text: 'As a repeat investor, I value speed and accuracy. Every transaction has closed on time with zero surprises. Highly recommend for investment properties.',
              property: 'Multi-Family Property - $1.6M'
            }
          ];

          function renderStars() {
            let s = '';
            for (let i = 0; i < 5; i++) {
              s += '<svg width="20" height="20" viewBox="0 0 24 24" fill="#FFD700" class="star-icon"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
            }
            return s;
          }

          let current = 0;
          function render(index) {
            const t = TESTIMONIALS[index];
            card.innerHTML =
              '<div class="testimonial-header">' +
                '<img src="' + t.image + '" alt="' + t.name + '" class="testimonial-image" />' +
                '<div class="testimonial-info">' +
                  '<h3 class="testimonial-name">' + t.name + '</h3>' +
                  '<p class="testimonial-role">' + t.role + '</p>' +
                  '<p class="testimonial-location">' + t.location + '</p>' +
                  '<div class="testimonial-rating">' + renderStars() + '</div>' +
                '</div>' +
              '</div>' +
              '<blockquote class="testimonial-text">"' + t.text + '"</blockquote>' +
              '<div class="testimonial-property"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>' + t.property + '</div>';
            indicators.forEach(function (ind, i) {
              if (i === index) ind.classList.add('active'); else ind.classList.remove('active');
            });
          }

          render(current);

          if (prevBtn) prevBtn.addEventListener('click', function () {
            current = (current - 1 + TESTIMONIALS.length) % TESTIMONIALS.length;
            render(current);
          });
          if (nextBtn) nextBtn.addEventListener('click', function () {
            current = (current + 1) % TESTIMONIALS.length;
            render(current);
          });
          indicators.forEach(function (ind, i) {
            ind.addEventListener('click', function () { current = i; render(current); });
          });
        }
      }
    }
  });

  DomComponents.addType('real-estate-brokerage-about-section', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' }
      },
      initialize() {
        const blockContent = getBlockContentById('real-estate-brokerage-about-section');
        this.set('content', blockContent);
      }
    }
  });

  DomComponents.addType('real-estate-property-search', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .property-search { background: #f5f5f7; padding: 64px 0; border-radius: 0; }
            .search-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
            .search-title { font-size: 48px; font-weight: 700; color: #1D1D1F; text-align: center; margin: 0 0 16px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; letter-spacing: -0.5px; }
            .search-subtitle { font-size: 20px; color: #6E6E73; text-align: center; margin: 0 0 48px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .search-form { background: white; border-radius: 20px; padding: 40px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08); border: 1px solid #f2f2f2; }
            .search-row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
            .search-field { display: flex; flex-direction: column; }
            .search-field label { font-size: 14px; font-weight: 600; color: #1D1D1F; margin-bottom: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; }
            .search-input, .search-select { padding: 16px 20px; border: 2px solid #f2f2f2; border-radius: 12px; font-size: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: white; transition: all 0.2s ease; color: #1D1D1F; }
            .search-input:focus, .search-select:focus { outline: none; border-color: #007AFF; box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1); }
            .search-input::placeholder { color: #8E8E93; }
            .price-range { display: flex; align-items: center; gap: 12px; }
            .price-input { flex: 1; padding: 16px 20px; border: 2px solid #f2f2f2; border-radius: 12px; font-size: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: white; transition: all 0.2s ease; color: #1D1D1F; }
            .price-input:focus { outline: none; border-color: #007AFF; box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1); }
            .price-separator { font-size: 16px; color: #6E6E73; font-weight: 500; white-space: nowrap; }
            .search-button { width: 100%; background: #007AFF; color: white; border: none; padding: 20px 32px; border-radius: 12px; font-size: 18px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 8px; }
            .search-button:hover { background: #0056CC; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0, 122, 255, 0.3); }
            .search-button:active { transform: translateY(0); }
            .search-button svg { width: 20px; height: 20px; }
            .quick-search { margin-top: 32px; text-align: center; }
            .quick-search-label { font-size: 16px; color: #6E6E73; font-weight: 500; margin-bottom: 16px; display: block; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .quick-search-buttons { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; }
            .quick-search-btn { background: white; border: 2px solid #f2f2f2; color: #1D1D1F; padding: 12px 20px; border-radius: 20px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .quick-search-btn:hover { border-color: #007AFF; color: #007AFF; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
            .search-results-grid { }
            @media (max-width: 768px) { .property-search { padding: 40px 0; } .search-container { padding: 0 16px; } .search-title { font-size: 32px; } .search-subtitle { font-size: 16px; } .search-form { padding: 20px; } .search-row { grid-template-columns: 1fr; gap: 16px; } .search-button { font-size: 16px; padding: 16px 16px; } .quick-search { margin-top: 24px; } }
          </style>
          <div class="property-search">
            <div class="search-container">
              <h2 class="search-title">Find Your Dream Home</h2>
              <p class="search-subtitle">Search properties in your preferred location</p>
              <form class="search-form">
                <div class="search-row">
                  <div class="search-field">
                    <label for="location">Location</label>
                    <input type="text" id="location" name="location" placeholder="City, neighborhood, or ZIP code" class="search-input" />
                  </div>
                  <div class="search-field">
                    <label for="propertyType">Property Type</label>
                    <select id="propertyType" name="propertyType" class="search-select">
                      <option value="">Any Type</option>
                      <option value="house">House</option>
                      <option value="condo">Condo</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="apartment">Apartment</option>
                      <option value="land">Land</option>
                    </select>
                  </div>
                </div>
                <div class="search-row">
                  <div class="search-field">
                    <label for="priceMin">Price Range</label>
                    <div class="price-range">
                      <input type="number" id="priceMin" name="priceMin" placeholder="Min" class="price-input" />
                      <span class="price-separator">to</span>
                      <input type="number" id="priceMax" name="priceMax" placeholder="Max" class="price-input" />
                    </div>
                  </div>
                  <div class="search-field">
                    <label for="bedrooms">Bedrooms</label>
                    <select id="bedrooms" name="bedrooms" class="search-select">
                      <option value="">Any</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                      <option value="5">5+</option>
                    </select>
                  </div>
                </div>
                <div class="search-row">
                  <div class="search-field">
                    <label for="bathrooms">Bathrooms</label>
                    <select id="bathrooms" name="bathrooms" class="search-select">
                      <option value="">Any</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                    </select>
                  </div>
                  <div class="search-field">
                    <label for="sqft">Square Feet</label>
                    <input type="number" id="sqft" name="sqft" placeholder="Min sqft" class="search-input" />
                  </div>
                </div>
                <button type="submit" class="search-button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/></svg>
                  Search Properties
                </button>
              </form>
              <div class="quick-search">
                <span class="quick-search-label">Popular searches:</span>
                <div class="quick-search-buttons">
                  <button type="button" class="quick-search-btn">Beverly Hills</button>
                  <button type="button" class="quick-search-btn">Malibu</button>
                  <button type="button" class="quick-search-btn">Santa Monica</button>
                  <button type="button" class="quick-search-btn">Manhattan Beach</button>
                </div>
              </div>
              <div class="search-results-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:20px;margin-top:32px;"></div>
            </div>
          </div>
        `,
        script: function () {
          const root = this;
          const form = root.querySelector('.search-form');
          const quickButtons = root.querySelectorAll('.quick-search-btn');
          const resultsEl = root.querySelector('.search-results-grid');

          function runSearch(locationOverride) {
            if (!form) return;
            const location = locationOverride != null ? locationOverride : ((form.querySelector('[name="location"]') || {}).value || '');
            const minPrice = parseFloat((form.querySelector('[name="priceMin"]') || {}).value);
            const maxPrice = parseFloat((form.querySelector('[name="priceMax"]') || {}).value);
            const beds = parseFloat((form.querySelector('[name="bedrooms"]') || {}).value);
            const baths = parseFloat((form.querySelector('[name="bathrooms"]') || {}).value);
            const minSqft = parseFloat((form.querySelector('[name="sqft"]') || {}).value);

            if (resultsEl) resultsEl.innerHTML = '<p style="text-align:center;color:#6E6E73;grid-column:1/-1;">Searching...</p>';

            const endpoint = location
              ? 'https://api.godreamx.com/api/repliers/search?q=' + encodeURIComponent(location) + '&format=raw'
              : 'https://api.godreamx.com/api/repliers/listings?sortBy=updatedOnDesc&format=raw';

            fetch(endpoint).then(function (res) {
              if (!res.ok) throw new Error('request failed: ' + res.status);
              return res.json();
            }).then(function (data) {
              let listings = Array.isArray(data) ? data : (data.listings || data.data || []);
              listings = listings.filter(function (p) {
                const price = parseFloat(p.listPrice);
                const bd = p.details && p.details.numBedrooms;
                const ba = p.details && p.details.numBathrooms;
                const sqft = p.details && p.details.sqft;
                if (!isNaN(minPrice) && !(price >= minPrice)) return false;
                if (!isNaN(maxPrice) && !(price <= maxPrice)) return false;
                if (!isNaN(beds) && !(bd >= beds)) return false;
                if (!isNaN(baths) && !(ba >= baths)) return false;
                if (!isNaN(minSqft) && !(sqft >= minSqft)) return false;
                return true;
              });
              if (!resultsEl) return;
              if (!listings.length) {
                resultsEl.innerHTML = '<p style="text-align:center;color:#6E6E73;grid-column:1/-1;">No properties found.</p>';
                return;
              }
              resultsEl.innerHTML = listings.slice(0, 12).map(function (property) {
                const a = property.address || {};
                const address = [a.streetNumber, a.streetName, a.streetSuffix].filter(Boolean).join(' ') + (a.city ? ', ' + a.city : '');
                const price = property.listPrice ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(parseFloat(property.listPrice)) : 'Price not available';
                const bd = (property.details && property.details.numBedrooms) || 'N/A';
                const ba = (property.details && property.details.numBathrooms) || 'N/A';
                return '<div style="background:#fff;border-radius:16px;padding:20px;box-shadow:0 2px 12px rgba(0,0,0,0.06);border:1px solid #f2f2f2;">' +
                  '<div style="font-weight:700;color:#1D1D1F;margin-bottom:6px;">' + address + '</div>' +
                  '<div style="color:#007AFF;font-weight:700;margin-bottom:8px;">' + price + '</div>' +
                  '<div style="color:#6E6E73;font-size:14px;">' + bd + ' bd &middot; ' + ba + ' ba</div>' +
                '</div>';
              }).join('');
            }).catch(function () {
              if (resultsEl) resultsEl.innerHTML = '<p style="text-align:center;color:#d93025;grid-column:1/-1;">Failed to load listings. Please try again.</p>';
            });
          }

          if (form && !form.dataset.gdxWired) {
            form.dataset.gdxWired = 'true';
            form.addEventListener('submit', function (e) {
              e.preventDefault();
              runSearch();
            });
          }

          quickButtons.forEach(function (btn) {
            if (btn.dataset.gdxWired) return;
            btn.dataset.gdxWired = 'true';
            btn.addEventListener('click', function () {
              const location = btn.textContent.trim();
              const locationInput = form ? form.querySelector('[name="location"]') : null;
              if (locationInput) locationInput.value = location;
              runSearch(location);
            });
          });
        }
      }
    }
  });

  // Shared by both map addTypes below -- loads Leaflet (free, no API key)
  // from a CDN on first use, plots real listings fetched from the public
  // /api/repliers proxy (see CLAUDE.md: deliberately open CORS, no key
  // shipped to the browser), and falls back to a clear "no data" message
  // instead of ever leaving the permanent dev placeholder that was here.
  const MAP_SCRIPT_BODY = `
    var root = this;
    function gdxEnsureLeaflet(cb) {
      if (window.L) { cb(); return; }
      window.__gdxLeafletCallbacks = window.__gdxLeafletCallbacks || [];
      window.__gdxLeafletCallbacks.push(cb);
      if (window.__gdxLeafletLoading) return;
      window.__gdxLeafletLoading = true;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      var scriptEl = document.createElement('script');
      scriptEl.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      scriptEl.onload = function () {
        (window.__gdxLeafletCallbacks || []).forEach(function (fn) { fn(); });
        window.__gdxLeafletCallbacks = [];
      };
      document.head.appendChild(scriptEl);
    }

    function gdxLatLng(property) {
      var lat = (property.map && (property.map.latitude || property.map.lat)) || property.latitude || property.lat;
      var lng = (property.map && (property.map.longitude || property.map.lng)) || property.longitude || property.lng;
      lat = parseFloat(lat); lng = parseFloat(lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return [lat, lng];
    }

    function gdxAddress(property) {
      if (!property.address) return 'Address not available';
      var a = property.address;
      return [a.streetNumber, a.streetName, a.streetSuffix].filter(Boolean).join(' ') + (a.city ? ', ' + a.city : '');
    }

    function gdxPrice(property) {
      if (!property.listPrice) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(parseFloat(property.listPrice));
    }

    function gdxRenderPins(map, markerLayer, listings) {
      markerLayer.clearLayers();
      var points = [];
      listings.forEach(function (property) {
        var latlng = gdxLatLng(property);
        if (!latlng) return;
        points.push(latlng);
        window.L.marker(latlng).addTo(markerLayer).bindPopup(
          '<div style="text-align:center;min-width:160px;">' +
            '<div style="font-weight:600;">' + gdxAddress(property) + '</div>' +
            (gdxPrice(property) ? '<div style="color:#007AFF;margin-top:4px;">' + gdxPrice(property) + '</div>' : '') +
          '</div>'
        );
      });
      if (points.length) map.fitBounds(points, { padding: [30, 30], maxZoom: 14 });
      return points.length;
    }

    var mapEl = root.querySelector('.leaflet-map');
    if (!mapEl || mapEl.dataset.gdxMapInit) return;
    mapEl.dataset.gdxMapInit = 'true';
    mapEl.innerHTML = '';
    mapEl.style.position = 'relative';
    mapEl.style.background = '#e5e5e7';

    var statusEl = document.createElement('div');
    statusEl.style.cssText = 'position:absolute;z-index:1000;top:12px;left:12px;background:#fff;padding:6px 12px;border-radius:8px;font-size:0.85rem;color:#6e6e73;box-shadow:0 2px 8px rgba(0,0,0,0.1);';
    statusEl.textContent = 'Loading map...';
    mapEl.appendChild(statusEl);

    gdxEnsureLeaflet(function () {
      if (mapEl.dataset.gdxMapReady) return;
      mapEl.dataset.gdxMapReady = 'true';
      var map = window.L.map(mapEl, { scrollWheelZoom: false }).setView([40.73, -73.99], 11);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);
      var markerLayer = window.L.layerGroup().addTo(map);

      function gdxLoadListings(query, filters) {
        statusEl.textContent = query ? 'Searching...' : 'Loading listings...';
        statusEl.style.display = 'block';
        var endpoint = query
          ? 'https://api.godreamx.com/api/repliers/search?q=' + encodeURIComponent(query) + '&format=raw'
          : 'https://api.godreamx.com/api/repliers/listings?sortBy=updatedOnDesc&format=raw';
        fetch(endpoint).then(function (res) {
          if (!res.ok) throw new Error('request failed: ' + res.status);
          return res.json();
        }).then(function (data) {
          var listings = Array.isArray(data) ? data : (data.listings || data.data || []);
          if (filters) {
            listings = listings.filter(function (p) {
              var price = parseFloat(p.listPrice);
              var bd = p.details && p.details.numBedrooms;
              var ba = p.details && p.details.numBathrooms;
              if (!isNaN(filters.minPrice) && !(price >= filters.minPrice)) return false;
              if (!isNaN(filters.maxPrice) && !(price <= filters.maxPrice)) return false;
              if (!isNaN(filters.beds) && !(bd >= filters.beds)) return false;
              if (!isNaN(filters.baths) && !(ba >= filters.baths)) return false;
              return true;
            });
          }
          var count = gdxRenderPins(map, markerLayer, listings);
          statusEl.textContent = count ? '' : 'No matching listings with map coordinates available.';
          statusEl.style.display = count ? 'none' : 'block';
        }).catch(function () {
          statusEl.textContent = 'Failed to load listings.';
          statusEl.style.display = 'block';
        });
      }

      mapEl._gdxLoadListings = gdxLoadListings;
      gdxLoadListings();
    });

    var form = root.querySelector('.map-search-filters');
    if (form && !form.dataset.gdxWired) {
      form.dataset.gdxWired = 'true';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var search = (form.querySelector('[name="search"]') || {}).value || '';
        var filters = {
          minPrice: parseFloat((form.querySelector('[name="minPrice"]') || {}).value),
          maxPrice: parseFloat((form.querySelector('[name="maxPrice"]') || {}).value),
          beds: parseFloat((form.querySelector('[name="beds"]') || {}).value),
          baths: parseFloat((form.querySelector('[name="baths"]') || {}).value)
        };
        if (mapEl._gdxLoadListings) {
          mapEl._gdxLoadListings(search, filters);
        } else {
          statusEl.textContent = 'Map is still loading, please try again in a moment.';
        }
      });
    }
  `;

  DomComponents.addType('real-estate-map-with-pins', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .map-with-pins { padding: 40px 0 24px 0; background: #f8f9fa; }
            .map-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .leaflet-map { width: 100%; height: 400px; border-radius: 18px; box-shadow: 0 4px 24px rgba(0,0,0,0.10); margin: 0 auto; max-width: 900px; }
            @media (max-width: 600px) { .leaflet-map { height: 250px; max-width: 98vw; } }
          </style>
          <section class="map-with-pins">
            <h2 class="map-title">Explore Properties on the Map</h2>
            <div class="leaflet-map"></div>
          </section>
        `,
        script: new Function(MAP_SCRIPT_BODY)
      }
    }
  });

  // Previously had no addType at all -- the block below was dropped as
  // plain parsed HTML, so no script (inline or otherwise) ever ran and
  // the map placeholder text and missing submit button were permanent.
  DomComponents.addType('real-estate-map-search-with-filters', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .map-search-with-filters { padding: 40px 0 24px 0; background: #f8f9fa; }
            .map-search-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .map-search-filters { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
            .map-search-filters input { font-size: 1rem; padding: 12px 16px; border: 1px solid #e5e5e7; border-radius: 10px; background: #fff; color: #1d1d1f; outline: none; transition: border 0.2s; min-width: 120px; }
            .map-search-filters input:focus { border: 1.5px solid #007AFF; }
            .map-search-submit-btn { background: #007AFF; color: #fff; border: none; border-radius: 10px; padding: 12px 28px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: background 0.2s; }
            .map-search-submit-btn:hover { background: #0056CC; }
            .leaflet-map { width: 100%; height: 400px; border-radius: 18px; box-shadow: 0 4px 24px rgba(0,0,0,0.10); margin: 0 auto; max-width: 900px; }
            @media (max-width: 600px) { .leaflet-map { height: 250px; max-width: 98vw; } .map-search-filters input { min-width: 80px; font-size: 0.95rem; } }
          </style>
          <section class="map-search-with-filters">
            <h2 class="map-search-title">Map Search with Filters</h2>
            <form class="map-search-filters">
              <input name="search" placeholder="Search address or city" />
              <input name="minPrice" placeholder="Min Price" type="number" min="0" />
              <input name="maxPrice" placeholder="Max Price" type="number" min="0" />
              <input name="beds" placeholder="Beds" type="number" min="0" />
              <input name="baths" placeholder="Baths" type="number" min="0" />
              <button type="submit" class="map-search-submit-btn">Search</button>
            </form>
            <div class="leaflet-map"></div>
          </section>
        `,
        script: new Function(MAP_SCRIPT_BODY)
      }
    }
  });

  // Generic CTA buttons below had no href/handler at all -- clicking them
  // was a complete no-op (a bare <button> with no enclosing <form> submits
  // nothing). Wire each to scroll to and focus the nearest real form on the
  // page (any contact/signup form a user has actually added), which is the
  // only generically-correct target for a plain "Get Started"/"Choose Plan"
  // CTA with no app-specific destination to link to.
  //
  // NOTE: each script below inlines this same scroll-to-form logic rather
  // than calling a shared helper -- confirmed directly (exported getJs(),
  // run standalone with no editor/outer scope present) that a script
  // referencing an outer-scope function throws "X is not defined" on the
  // real published page, since GrapesJS serializes each component's script
  // via toString() with no closure over this file's surrounding scope.

  DomComponents.addType('react-hero-section', {
    model: {
      defaults: {
        tagName: 'div',
        content: `
          <section style="padding: 80px 20px; text-align: center; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;">
            <div style="max-width: 800px; margin: 0 auto;">
              <h1 style="font-size: 3rem; margin-bottom: 20px;">Welcome to Our Platform</h1>
              <p style="font-size: 1.2rem; margin-bottom: 30px; opacity: 0.9;">Build amazing websites with our React-based web builder</p>
              <button type="button" style="background: white; color: #667eea; padding: 12px 30px; border: none; border-radius: 25px; font-size: 1.1rem; cursor: pointer;">Get Started</button>
            </div>
          </section>
        `,
        script: function () {
          const root = this;
          const btn = root.querySelector('button');
          if (!btn || btn.dataset.gdxWired) return;
          btn.dataset.gdxWired = 'true';
          btn.addEventListener('click', function () {
            const form = document.querySelector('.agent-form, form');
            if (form) {
              form.scrollIntoView({ behavior: 'smooth', block: 'center' });
              const firstInput = form.querySelector('input, textarea, select');
              if (firstInput) firstInput.focus();
            }
          });
        }
      }
    }
  });

  DomComponents.addType('react-cta-section', {
    model: {
      defaults: {
        tagName: 'div',
        content: `
          <section style="padding: 60px 20px; background: #667eea; color: white; text-align: center;">
            <div style="max-width: 600px; margin: 0 auto;">
              <h2 style="font-size: 2.5rem; margin-bottom: 20px;">Ready to Get Started?</h2>
              <p style="font-size: 1.1rem; margin-bottom: 30px; opacity: 0.9;">Join thousands of users who are already building amazing websites with our platform.</p>
              <div style="display: flex; gap: 15px; justify-content: center; flex-wrap: wrap;">
                <button type="button" class="gdx-cta-primary" style="background: white; color: #667eea; padding: 12px 30px; border: none; border-radius: 25px; font-size: 1.1rem; cursor: pointer;">Start Free Trial</button>
                <button type="button" class="gdx-cta-secondary" style="background: transparent; color: white; padding: 12px 30px; border: 2px solid white; border-radius: 25px; font-size: 1.1rem; cursor: pointer;">Learn More</button>
              </div>
            </div>
          </section>
        `,
        script: function () {
          const root = this;
          const startBtn = root.querySelector('.gdx-cta-primary');
          const learnBtn = root.querySelector('.gdx-cta-secondary');

          if (startBtn && !startBtn.dataset.gdxWired) {
            startBtn.dataset.gdxWired = 'true';
            startBtn.addEventListener('click', function () {
              const form = document.querySelector('.agent-form, form');
              if (form) {
                form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const firstInput = form.querySelector('input, textarea, select');
                if (firstInput) firstInput.focus();
              }
            });
          }

          if (learnBtn && !learnBtn.dataset.gdxWired) {
            learnBtn.dataset.gdxWired = 'true';
            learnBtn.addEventListener('click', function () {
              const section = root.querySelector('section');
              const next = section ? section.nextElementSibling : null;
              if (next) {
                next.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
              }
            });
          }
        }
      }
    }
  });

  DomComponents.addType('react-pricing-card', {
    model: {
      defaults: {
        tagName: 'div',
        content: `
          <div style="padding: 40px; border: 2px solid #e1e5e9; border-radius: 12px; text-align: center; background: white; position: relative;">
            <div style="position: absolute; top: -15px; left: 50%; transform: translateX(-50%); background: #667eea; color: white; padding: 8px 20px; border-radius: 20px; font-size: 0.9rem;">POPULAR</div>
            <h3 style="font-size: 1.5rem; margin-bottom: 10px; color: #333;">Pro Plan</h3>
            <div style="font-size: 3rem; font-weight: bold; color: #667eea; margin-bottom: 20px;">$29<span style="font-size: 1rem; color: #666;">/month</span></div>
            <ul style="list-style: none; padding: 0; margin-bottom: 30px; text-align: left;">
              <li style="padding: 8px 0; color: #555;">✓ Unlimited projects</li>
              <li style="padding: 8px 0; color: #555;">✓ Advanced features</li>
              <li style="padding: 8px 0; color: #555;">✓ Priority support</li>
              <li style="padding: 8px 0; color: #555;">✓ Custom domains</li>
            </ul>
            <button type="button" style="background: #667eea; color: white; padding: 12px 30px; border: none; border-radius: 25px; font-size: 1.1rem; cursor: pointer; width: 100%;">Choose Plan</button>
          </div>
        `,
        script: function () {
          const root = this;
          const btn = root.querySelector('button');
          if (!btn || btn.dataset.gdxWired) return;
          btn.dataset.gdxWired = 'true';
          btn.addEventListener('click', function () {
            const form = document.querySelector('.agent-form, form');
            if (form) {
              form.scrollIntoView({ behavior: 'smooth', block: 'center' });
              const firstInput = form.querySelector('input, textarea, select');
              if (firstInput) firstInput.focus();
            }
          });
        }
      }
    }
  });

  DomComponents.addType('real-estate-property-comparison-table', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .property-comparison-table { padding: 40px 0 24px 0; background: #f8f9fa; }
            .comparison-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .comparison-select { display: flex; gap: 24px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
            .comparison-checkbox { display: flex; align-items: center; gap: 10px; background: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 8px 16px; cursor: pointer; font-size: 1rem; font-family: inherit; transition: box-shadow 0.15s; }
            .comparison-checkbox:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
            .comparison-thumb { width: 48px; height: 36px; object-fit: cover; border-radius: 6px; }
            .comparison-table-wrapper { overflow-x: auto; max-width: 100vw; }
            .comparison-table { width: 100%; max-width: 900px; margin: 0 auto; border-collapse: collapse; background: #fff; border-radius: 18px; box-shadow: 0 4px 24px rgba(0,0,0,0.10); overflow: hidden; }
            .comparison-table th, .comparison-table td { padding: 14px 18px; text-align: center; border-bottom: 1px solid #e5e5e7; font-size: 1rem; }
            .comparison-table th { background: #f2f2f7; font-weight: 600; position: sticky; top: 0; z-index: 1; }
            .comparison-table tr.diff td { background: #fffbe6; color: #b26a00; font-weight: 600; }
          </style>
          <section class="property-comparison-table">
            <h2 class="comparison-title">Compare Properties</h2>
            <div class="comparison-select">
              <label class="comparison-checkbox"><input type="checkbox" /> <img src="https://images.unsplash.com/photo-1460518451285-97b6aa326961?w=400&q=80&auto=format&fit=crop" class="comparison-thumb" /> <span>123 Apple Lane, Cupertino, CA</span></label>
              <label class="comparison-checkbox"><input type="checkbox" /> <img src="https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=400&q=80&auto=format&fit=crop" class="comparison-thumb" /> <span>456 Silicon Ave, Mountain View, CA</span></label>
              <label class="comparison-checkbox"><input type="checkbox" /> <img src="https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?w=400&q=80&auto=format&fit=crop" class="comparison-thumb" /> <span>789 Infinite Loop, Palo Alto, CA</span></label>
            </div>
            <div class="comparison-table-wrapper">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>123 Apple Lane, Cupertino, CA</th>
                    <th>456 Silicon Ave, Mountain View, CA</th>
                    <th>789 Infinite Loop, Palo Alto, CA</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="diff"><td>Price</td><td>$1,250,000</td><td>$950,000</td><td>$2,100,000</td></tr>
                  <tr><td>Beds</td><td>4</td><td>3</td><td>5</td></tr>
                  <tr><td>Baths</td><td>3</td><td>2</td><td>4</td></tr>
                  <tr><td>Sqft</td><td>2,800</td><td>2,100</td><td>3,500</td></tr>
                  <tr><td>Pool</td><td>✔️</td><td></td><td>✔️</td></tr>
                  <tr><td>Garage</td><td>✔️</td><td>✔️</td><td>✔️</td></tr>
                  <tr><td>Fireplace</td><td>✔️</td><td></td><td></td></tr>
                  <tr><td>Garden</td><td></td><td>✔️</td><td></td></tr>
                  <tr><td>Home Theater</td><td></td><td></td><td>✔️</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        `,
        script: function () {
          const root = this;
          const table = root.querySelector('.comparison-table');
          if (!table || table.dataset.gdxWired) return;
          table.dataset.gdxWired = 'true';
          const checkboxes = root.querySelectorAll('.comparison-checkbox input[type="checkbox"]');
          checkboxes.forEach(function (cb, i) {
            cb.checked = true;
            cb.addEventListener('change', function () {
              const colIndex = i + 1;
              const rows = table.querySelectorAll('tr');
              rows.forEach(function (row) {
                const cell = row.children[colIndex];
                if (cell) cell.style.display = cb.checked ? '' : 'none';
              });
            });
          });
        }
      }
    }
  });

  DomComponents.addType('real-estate-contract-offer-simulator', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .contract-offer-simulator { padding: 40px 0 24px 0; background: #f8f9fa; }
            .sim-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .sim-form { max-width: 420px; margin: 0 auto 24px auto; display: flex; flex-direction: column; gap: 18px; background: #fff; border-radius: 18px; box-shadow: 0 4px 24px rgba(0,0,0,0.10); padding: 32px 24px; }
            .sim-row { display: flex; flex-direction: column; gap: 6px; }
            .sim-row label { font-size: 1rem; font-weight: 500; color: #1d1d1f; }
            .sim-form input, .sim-form textarea { font-size: 1rem; padding: 12px 14px; border: 1px solid #e5e5e7; border-radius: 10px; font-family: inherit; background: #f9f9fa; color: #1d1d1f; outline: none; transition: border 0.2s; }
            .sim-form input:focus, .sim-form textarea:focus { border: 1.5px solid #007AFF; }
            .sim-submit { margin-top: 10px; font-size: 1.1rem; font-weight: 600; background: #007AFF; color: #fff; border: none; border-radius: 10px; padding: 14px 0; cursor: pointer; box-shadow: 0 2px 8px rgba(0,122,255,0.08); transition: background 0.15s; }
            .sim-submit:hover { background: #005ecb; }
            .sim-preview { max-width: 420px; margin: 0 auto 0 auto; background: #fff; border-radius: 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 24px 18px; margin-bottom: 12px; }
            .sim-preview h3 { font-size: 1.2rem; font-weight: 600; margin-bottom: 12px; }
            .sim-preview ul { list-style: none; padding: 0; margin: 0; }
            .sim-preview li { font-size: 1rem; margin-bottom: 6px; }
            .sim-success { text-align: center; font-size: 1.1rem; color: #007AFF; margin-top: 18px; animation: pop 0.3s; }
            @keyframes pop { 0% { transform: scale(0.7); opacity: 0; } 80% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); } }
            @media (max-width: 600px) { .sim-form, .sim-preview { max-width: 98vw; padding: 18px 6px; } }
          </style>
          <section class="contract-offer-simulator">
            <h2 class="sim-title">Contract Offer Simulator</h2>
            <form class="sim-form">
              <div class="sim-row"><label>Offer Price</label><input class="sim-price" placeholder="$" type="number" min="0" /></div>
              <div class="sim-row"><label>Deposit Amount</label><input class="sim-deposit" placeholder="$" type="number" min="0" /></div>
              <div class="sim-row"><label>Closing Date</label><input class="sim-closing" type="date" /></div>
              <div class="sim-row"><label>Contingencies</label><input class="sim-contingencies" placeholder="e.g. Inspection, Financing" /></div>
              <div class="sim-row"><label>Notes</label><textarea class="sim-notes" placeholder="Additional notes or terms" rows="2"></textarea></div>
              <button class="sim-submit" type="button">Submit Offer</button>
            </form>
            <div class="sim-preview">
              <h3>Offer Summary</h3>
              <ul>
                <li><strong>Price:</strong> —</li>
                <li><strong>Deposit:</strong> —</li>
                <li><strong>Closing Date:</strong> —</li>
                <li><strong>Contingencies:</strong> —</li>
                <li><strong>Notes:</strong> —</li>
              </ul>
            </div>
          </section>
        `,
        script: function () {
          const root = this;
          const form = root.querySelector('.sim-form');
          const preview = root.querySelector('.sim-preview ul');
          const submitBtn = root.querySelector('.sim-submit');
          if (!form || form.dataset.gdxWired) return;
          form.dataset.gdxWired = 'true';

          const priceInput = root.querySelector('.sim-price');
          const depositInput = root.querySelector('.sim-deposit');
          const closingInput = root.querySelector('.sim-closing');
          const contingenciesInput = root.querySelector('.sim-contingencies');
          const notesInput = root.querySelector('.sim-notes');

          function formatCurrency(val) {
            const n = parseFloat(val);
            if (isNaN(n)) return '—';
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
          }

          function updatePreview() {
            const items = preview.querySelectorAll('li');
            if (items[0]) items[0].innerHTML = '<strong>Price:</strong> ' + formatCurrency(priceInput.value);
            if (items[1]) items[1].innerHTML = '<strong>Deposit:</strong> ' + formatCurrency(depositInput.value);
            if (items[2]) items[2].innerHTML = '<strong>Closing Date:</strong> ' + (closingInput.value || '—');
            if (items[3]) items[3].innerHTML = '<strong>Contingencies:</strong> ' + (contingenciesInput.value || '—');
            if (items[4]) items[4].innerHTML = '<strong>Notes:</strong> ' + (notesInput.value || '—');
          }

          [priceInput, depositInput, closingInput, contingenciesInput, notesInput].forEach(function (el) {
            if (el) el.addEventListener('input', updatePreview);
          });

          if (submitBtn) {
            submitBtn.addEventListener('click', function () {
              updatePreview();
              let successEl = root.querySelector('.sim-success');
              if (!successEl) {
                successEl = document.createElement('div');
                successEl.className = 'sim-success';
                preview.parentElement.appendChild(successEl);
              }
              successEl.textContent = 'Offer summary updated below.';
              setTimeout(function () {
                if (successEl) successEl.textContent = '';
              }, 3000);
            });
          }
        }
      }
    }
  });

  DomComponents.addType('real-estate-property-detail-page', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .property-detail-page { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1d1d1f; background: #f5f5f7; }
            .property-hero { position: relative; background: #000; overflow: hidden; }
            .hero-image-container { position: relative; height: 70vh; min-height: 500px; }
            .hero-image { width: 100%; height: 100%; object-fit: cover; }
            .image-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%); display: flex; align-items: flex-end; padding: 60px 20px; }
            .hero-content { color: white; max-width: 1200px; margin: 0 auto; width: 100%; }
            .property-title { font-size: 3.5rem; font-weight: 700; margin: 0 0 10px 0; line-height: 1.1; }
            .property-address { font-size: 1.2rem; margin: 0 0 20px 0; opacity: 0.9; }
            .price-badge { display: inline-block; background: #007aff; color: white; padding: 12px 24px; border-radius: 25px; font-size: 1.3rem; font-weight: 600; }
            .image-gallery { display: flex; gap: 10px; padding: 20px; background: white; overflow-x: auto; }
            .gallery-thumbnail { flex-shrink: 0; width: 120px; height: 80px; border-radius: 8px; overflow: hidden; cursor: pointer; transition: all 0.3s ease; border: 2px solid transparent; }
            .gallery-thumbnail.active { border-color: #007aff; transform: scale(1.05); }
            .gallery-thumbnail img { width: 100%; height: 100%; object-fit: cover; }
            .property-details { padding: 60px 0; }
            .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
            .details-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 40px; }
            .main-content { background: white; border-radius: 16px; padding: 40px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
            .key-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 40px; padding-bottom: 30px; border-bottom: 1px solid #e5e5e7; }
            .stat { text-align: center; }
            .stat-number { display: block; font-size: 2rem; font-weight: 700; color: #007aff; margin-bottom: 5px; }
            .stat-label { font-size: 0.9rem; color: #86868b; text-transform: uppercase; letter-spacing: 0.5px; }
            .sidebar { display: flex; flex-direction: column; gap: 20px; }
            .agent-card { background: white; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
            .agent-header { display: flex; align-items: center; gap: 15px; margin-bottom: 25px; }
            .agent-image { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; }
            .agent-info h3 { margin: 0 0 5px 0; font-size: 1.2rem; font-weight: 600; }
            .agent-info p { margin: 0; color: #86868b; font-size: 0.9rem; }
            .agent-contact { display: flex; flex-direction: column; gap: 12px; }
            .contact-btn { padding: 12px 20px; border-radius: 8px; text-decoration: none; text-align: center; font-weight: 500; transition: all 0.3s ease; border: none; cursor: pointer; font-size: 0.95rem; }
            .contact-btn.phone { background: #f5f5f7; color: #1d1d1f; }
            .contact-btn.email { background: #f5f5f7; color: #1d1d1f; }
            .contact-btn.primary { background: #007aff; color: white; }
            @media (max-width: 768px) { .details-grid { grid-template-columns: 1fr; } .key-stats { grid-template-columns: repeat(2, 1fr); } }
          </style>
          <div class="property-detail-page">
            <section class="property-hero">
              <div class="hero-image-container">
                <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&h=800&fit=crop" alt="Luxury Modern Villa" class="hero-image" />
                <div class="image-overlay">
                  <div class="hero-content">
                    <h1 class="property-title">Luxury Modern Villa</h1>
                    <p class="property-address">1234 Luxury Lane, Beverly Hills, CA 90210</p>
                    <div class="price-badge">$2,850,000</div>
                  </div>
                </div>
              </div>
              <div class="image-gallery">
                <div class="gallery-thumbnail active"><img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&h=800&fit=crop" /></div>
                <div class="gallery-thumbnail"><img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&h=800&fit=crop" /></div>
                <div class="gallery-thumbnail"><img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&h=800&fit=crop" /></div>
              </div>
            </section>
            <section class="property-details">
              <div class="container">
                <div class="details-grid">
                  <div class="main-content">
                    <div class="key-stats">
                      <div class="stat"><span class="stat-number">5</span><span class="stat-label">Bedrooms</span></div>
                      <div class="stat"><span class="stat-number">4.5</span><span class="stat-label">Bathrooms</span></div>
                      <div class="stat"><span class="stat-number">4,200</span><span class="stat-label">Sq Ft</span></div>
                      <div class="stat"><span class="stat-number">0.8</span><span class="stat-label">Lot Size</span></div>
                    </div>
                  </div>
                  <div class="sidebar">
                    <div class="agent-card">
                      <div class="agent-header">
                        <img src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face" alt="Sarah Johnson" class="agent-image" />
                        <div class="agent-info">
                          <h3>Sarah Johnson</h3>
                          <p>Senior Real Estate Agent</p>
                          <p>15+ years experience</p>
                        </div>
                      </div>
                      <div class="agent-contact">
                        <a href="tel:+13105550123" class="contact-btn phone">+1 (310) 555-0123</a>
                        <a href="mailto:sarah.johnson@dreamxrealty.com" class="contact-btn email">sarah.johnson@dreamxrealty.com</a>
                        <button type="button" class="contact-btn primary">Schedule Viewing</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        `,
        script: function () {
          const root = this;
          const btn = root.querySelector('.contact-btn.primary');
          if (btn && !btn.dataset.gdxWired) {
            btn.dataset.gdxWired = 'true';
            const emailLink = root.querySelector('.contact-btn.email[href^="mailto:"]');
            const email = emailLink ? emailLink.getAttribute('href').replace('mailto:', '') : '';
            btn.addEventListener('click', function () {
              const form = document.querySelector('.agent-form, form');
              if (form) {
                form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const firstInput = form.querySelector('input, textarea, select');
                if (firstInput) firstInput.focus();
              } else if (email) {
                window.location.href = 'mailto:' + email + '?subject=' + encodeURIComponent('Schedule a Viewing');
              }
            });
          }

          const thumbs = root.querySelectorAll('.gallery-thumbnail');
          const heroImg = root.querySelector('.hero-image');
          thumbs.forEach(function (thumb) {
            if (thumb.dataset.gdxWired) return;
            thumb.dataset.gdxWired = 'true';
            thumb.addEventListener('click', function () {
              thumbs.forEach(function (t) { t.classList.remove('active'); });
              thumb.classList.add('active');
              const img = thumb.querySelector('img');
              if (img && heroImg) heroImg.src = img.src.replace(/w=\d+&h=\d+/, 'w=1200&h=800');
            });
          });
        }
      }
    }
  });

  DomComponents.addType('real-estate-property-comparison-page', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        attributes: { class: 'template-component-wrapper' },
        content: `
          <style>
            .property-comparison-page { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1d1d1f; background: #f5f5f7; min-height: 100vh; }
            .comparison-header { background: linear-gradient(135deg, #007aff 0%, #5856d6 100%); color: white; padding: 60px 0; }
            .header-content { display: flex; justify-content: space-between; align-items: center; }
            .header-left h1 { font-size: 3rem; font-weight: 700; margin: 0 0 10px 0; line-height: 1.1; }
            .header-left p { font-size: 1.2rem; margin: 0; opacity: 0.9; }
            .view-controls { display: flex; gap: 8px; background: rgba(255,255,255,0.1); border-radius: 12px; padding: 4px; backdrop-filter: blur(10px); }
            .view-btn { background: none; border: none; color: white; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 500; transition: all 0.3s ease; }
            .view-btn.active { background: rgba(255,255,255,0.2); backdrop-filter: blur(10px); }
            .property-selection { padding: 40px 0; background: white; }
            .selection-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
            .selection-header h2 { font-size: 1.8rem; font-weight: 600; margin: 0; color: #1d1d1f; }
            .selection-count { color: #86868b; font-size: 0.95rem; }
            .property-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
            .property-card { background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); transition: all 0.3s ease; cursor: pointer; border: 3px solid transparent; }
            .property-card.selected { border-color: #007aff; box-shadow: 0 8px 30px rgba(0,122,255,0.2); }
            .property-image { position: relative; height: 200px; overflow: hidden; }
            .property-image img { width: 100%; height: 100%; object-fit: cover; }
            .property-info { padding: 20px; }
            .property-info h3 { font-size: 1.1rem; font-weight: 600; margin: 0 0 8px 0; color: #1d1d1f; }
            .property-price { font-size: 1.3rem; font-weight: 700; color: #007aff; margin-bottom: 8px; }
            .property-address { color: #86868b; font-size: 0.9rem; margin: 0 0 12px 0; line-height: 1.4; }
            .property-stats { display: flex; gap: 12px; font-size: 0.85rem; color: #86868b; }
            .comparison-results { padding: 40px 0; }
            .results-header { text-align: center; margin-bottom: 40px; }
            .results-header h2 { font-size: 2rem; font-weight: 600; margin: 0 0 10px 0; color: #1d1d1f; }
            .results-header p { color: #86868b; font-size: 1.1rem; margin: 0; }
            .side-by-side-comparison { background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
            .comparison-grid { display: grid; grid-template-columns: 200px repeat(auto-fit, minmax(250px, 1fr)); }
            .comparison-label { background: #f5f5f7; padding: 15px 20px; font-weight: 500; color: #1d1d1f; display: flex; align-items: center; border-bottom: 1px solid #e5e5e7; }
            .comparison-value { padding: 15px 20px; display: flex; align-items: center; justify-content: center; border-bottom: 1px solid #f5f5f7; text-align: center; font-weight: 500; }
            .comparison-section-header { grid-column: 1 / -1; background: #1d1d1f; color: white; padding: 15px 20px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.9rem; }
            .container { max-width: 1400px; margin: 0 auto; padding: 0 20px; }
            @media (max-width: 768px) { .header-content { flex-direction: column; gap: 20px; text-align: center; } .property-grid { grid-template-columns: 1fr; } }
          </style>
          <div class="property-comparison-page">
            <section class="comparison-header">
              <div class="container">
                <div class="header-content">
                  <div class="header-left">
                    <h1>Compare Properties</h1>
                    <p>Select up to 3 properties to compare side by side</p>
                  </div>
                  <div class="header-right">
                    <div class="view-controls">
                      <button type="button" class="view-btn active">Side by Side</button>
                      <button type="button" class="view-btn">Table View</button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section class="property-selection">
              <div class="container">
                <div class="selection-header">
                  <h2>Select Properties to Compare</h2>
                  <span class="selection-count">0/3 selected</span>
                </div>
                <div class="property-grid">
                  <div class="property-card">
                    <div class="property-image">
                      <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&h=400&fit=crop" alt="Luxury Modern Villa" />
                    </div>
                    <div class="property-info">
                      <h3>Luxury Modern Villa</h3>
                      <div class="property-price">$2,850,000</div>
                      <p class="property-address">1234 Luxury Lane, Beverly Hills, CA 90210</p>
                      <div class="property-stats">
                        <span>5 beds</span>
                        <span>4.5 baths</span>
                        <span>4,200 sq ft</span>
                      </div>
                    </div>
                  </div>
                  <div class="property-card">
                    <div class="property-image">
                      <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop" alt="Contemporary Estate" />
                    </div>
                    <div class="property-info">
                      <h3>Contemporary Estate</h3>
                      <div class="property-price">$1,950,000</div>
                      <p class="property-address">5678 Sunset Blvd, Beverly Hills, CA 90210</p>
                      <div class="property-stats">
                        <span>4 beds</span>
                        <span>3.5 baths</span>
                        <span>3,800 sq ft</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section class="comparison-results">
              <div class="container">
                <div class="results-header">
                  <h2>Property Comparison</h2>
                  <p>Select properties to see detailed comparison</p>
                </div>
                <div class="side-by-side-comparison">
                  <div class="comparison-grid">
                    <div class="comparison-section-header">Select properties to compare</div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        `,
        script: function () {
          const root = this;
          const cards = root.querySelectorAll('.property-card');
          const countEl = root.querySelector('.selection-count');
          const viewBtns = root.querySelectorAll('.view-btn');
          const sideBtn = viewBtns[0];
          const tableBtn = viewBtns[1];
          const resultsContainer = root.querySelector('.side-by-side-comparison');
          if (!resultsContainer || resultsContainer.dataset.gdxWired) return;
          resultsContainer.dataset.gdxWired = 'true';

          let viewMode = 'side';

          function extractData(card) {
            const titleEl = card.querySelector('h3');
            const priceEl = card.querySelector('.property-price');
            const addressEl = card.querySelector('.property-address');
            const stats = Array.prototype.slice.call(card.querySelectorAll('.property-stats span')).map(function (s) { return s.textContent; });
            return {
              title: titleEl ? titleEl.textContent : '',
              price: priceEl ? priceEl.textContent : '',
              address: addressEl ? addressEl.textContent : '',
              beds: stats[0] || '',
              baths: stats[1] || '',
              sqft: stats[2] || ''
            };
          }

          function getSelected() {
            return Array.prototype.filter.call(cards, function (c) { return c.classList.contains('selected'); }).map(extractData);
          }

          const FIELDS = ['price', 'beds', 'baths', 'sqft', 'address'];
          const LABELS = { price: 'Price', beds: 'Beds', baths: 'Baths', sqft: 'Sqft', address: 'Address' };

          function render() {
            const selected = getSelected();
            if (countEl) countEl.textContent = selected.length + '/3 selected';

            if (!selected.length) {
              resultsContainer.innerHTML = '<div class="comparison-grid"><div class="comparison-section-header">Select properties to compare</div></div>';
              return;
            }

            if (viewMode === 'table') {
              resultsContainer.innerHTML =
                '<table style="width:100%;border-collapse:collapse;">' +
                  '<tr><th style="padding:15px 20px;background:#f5f5f7;text-align:left;">Field</th>' +
                    selected.map(function (p) { return '<th style="padding:15px 20px;background:#f5f5f7;">' + p.title + '</th>'; }).join('') +
                  '</tr>' +
                  FIELDS.map(function (field) {
                    return '<tr><td style="padding:15px 20px;font-weight:600;border-bottom:1px solid #e5e5e7;">' + LABELS[field] + '</td>' +
                      selected.map(function (p) { return '<td style="padding:15px 20px;text-align:center;border-bottom:1px solid #e5e5e7;">' + p[field] + '</td>'; }).join('') +
                    '</tr>';
                  }).join('') +
                '</table>';
            } else {
              resultsContainer.innerHTML =
                '<div class="comparison-grid" style="grid-template-columns: 200px repeat(' + selected.length + ', minmax(200px, 1fr));">' +
                  '<div class="comparison-section-header">Comparison</div>' +
                  FIELDS.map(function (field) {
                    return '<div class="comparison-label">' + LABELS[field] + '</div>' +
                      selected.map(function (p) { return '<div class="comparison-value">' + p[field] + '</div>'; }).join('');
                  }).join('') +
                '</div>';
            }
          }

          cards.forEach(function (card) {
            card.addEventListener('click', function () {
              if (!card.classList.contains('selected') && getSelected().length >= 3) return;
              card.classList.toggle('selected');
              render();
            });
          });

          if (sideBtn && tableBtn) {
            sideBtn.addEventListener('click', function () {
              viewMode = 'side';
              sideBtn.classList.add('active');
              tableBtn.classList.remove('active');
              render();
            });
            tableBtn.addEventListener('click', function () {
              viewMode = 'table';
              tableBtn.classList.add('active');
              sideBtn.classList.remove('active');
              render();
            });
          }

          render();
        }
      }
    }
  });

  // Update the getter function after blocks are registered (with proper typing)
  const updateGetBlockContentById = (id: string): string => {
    const block = BlockManager.get(id);
    return block ? block.get('content') as string : '<div>Content not found</div>';
  };
  getBlockContentById = updateGetBlockContentById;

  // Layout Blocks
  const layoutBlocks = [
    {
      id: 'react-container',
      label: 'Container',
      category: categories.layout,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: `
        <div style="max-width: 1200px; margin: 0 auto; padding: 0 20px;">
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Container Content
          </div>
        </div>
      `
    },
    {
      id: 'react-grid-2',
      label: '2 Column Grid',
      category: categories.layout,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z"/>
      </svg>`,
      content: `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding: 20px;">
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Column 1
          </div>
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Column 2
          </div>
        </div>
      `
    },
    {
      id: 'react-grid-3',
      label: '3 Column Grid',
      category: categories.layout,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M3 3v6h6V3H3zm4 4H5V5h2v2zm-4 4v6h6v-6H3zm4 4H5v-2h2v2zm4-12v6h6V3h-6zm4 4h-2V5h2v2zm-4 4v6h6v-6h-6zm4 4h-2v-2h2v2zm4-12v6h6V3h-6zm4 4h-2V5h2v2zm-4 4v6h6v-6h-6zm4 4h-2v-2h2v2z"/>
      </svg>`,
      content: `
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; padding: 20px;">
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Column 1
          </div>
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Column 2
          </div>
          <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
            Column 3
          </div>
        </div>
      `
    },
    {
      id: 'react-section',
      label: 'Section',
      category: categories.layout,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
      </svg>`,
      content: `
        <section style="padding: 60px 20px; background: #f8f9fa;">
          <div style="max-width: 1200px; margin: 0 auto;">
            <div style="min-height: 100px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">
              Section Content
            </div>
          </div>
        </section>
      `
    }
  ]

  // Basic Elements
  const basicBlocks = [
    {
      id: 'react-div',
      label: 'Div',
      category: categories.basic,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M3 3v6h6V3H3zm4 4H5V5h2v2zm-4 4v6h6v-6H3zm4 4H5v-2h2v2zm4-12v6h6V3h-6zm4 4h-2V5h2v2zm-4 4v6h6v-6h-6zm4 4h-2v-2h2v2z"/>
      </svg>`,
      content: '<div style="min-height: 50px; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; color: #666;">Div Element</div>'
    },
    {
      id: 'react-span',
      label: 'Span',
      category: categories.basic,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M9 4v3h5v12h3V7h5V4H9z"/>
      </svg>`,
      content: '<span style="padding: 5px 10px; background: #e9ecef; border-radius: 4px;">Span Element</span>'
    },
    {
      id: 'react-button',
      label: 'Button',
      category: categories.basic,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: '<button style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">Button</button>'
    },
    {
      id: 'react-link',
      label: 'Link',
      category: categories.basic,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
      </svg>`,
      content: '<a href="#" style="color: #007bff; text-decoration: none;">Link Text</a>'
    }
  ]

  // Typography Blocks
  const typographyBlocks = [
    {
      id: 'react-h1',
      label: 'Heading 1',
      category: categories.typography,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M5 4v3h5v12h3V7h5V4H5z"/>
      </svg>`,
      content: '<h1 style="margin: 0; font-size: 2.5rem; font-weight: bold; color: #333;">Heading 1</h1>'
    },
    {
      id: 'react-h2',
      label: 'Heading 2',
      category: categories.typography,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M5 4v3h5v12h3V7h5V4H5z"/>
      </svg>`,
      content: '<h2 style="margin: 0; font-size: 2rem; font-weight: bold; color: #333;">Heading 2</h2>'
    },
    {
      id: 'react-h3',
      label: 'Heading 3',
      category: categories.typography,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M5 4v3h5v12h3V7h5V4H5z"/>
      </svg>`,
      content: '<h3 style="margin: 0; font-size: 1.5rem; font-weight: bold; color: #333;">Heading 3</h3>'
    },
    {
      id: 'react-paragraph',
      label: 'Paragraph',
      category: categories.typography,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M9 4v3h5v12h3V7h5V4H9z"/>
      </svg>`,
      content: '<p style="margin: 0; line-height: 1.6; color: #666;">This is a paragraph with some sample text to demonstrate the typography styling.</p>'
    },
    {
      id: 'react-text-block',
      label: 'Text Block',
      category: categories.typography,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
      </svg>`,
      content: `
        <div style="max-width: 600px;">
          <h2 style="margin-bottom: 20px; font-size: 2rem; font-weight: bold; color: #333;">Text Block Title</h2>
          <p style="margin-bottom: 15px; line-height: 1.6; color: #666;">This is a comprehensive text block with multiple paragraphs to demonstrate rich typography.</p>
          <p style="line-height: 1.6; color: #666;">You can add multiple paragraphs, headings, and other text elements to create engaging content.</p>
        </div>
      `
    }
  ]

  // Media Blocks
  const mediaBlocks = [
    {
      id: 'react-image',
      label: 'Image',
      category: categories.media,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
      </svg>`,
      content: '<img src="https://via.placeholder.com/400x300" alt="Placeholder Image" style="max-width: 100%; height: auto; border-radius: 8px;" />'
    },
    {
      id: 'react-video',
      label: 'Video',
      category: categories.media,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
      </svg>`,
      content: '<video controls poster="https://via.placeholder.com/640x360?text=Click+to+set+video" style="max-width: 100%; height: auto; border-radius: 8px;"><source src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4" type="video/mp4">Your browser does not support the video tag.</video>'
    },
    {
      id: 'react-iframe',
      label: 'Iframe',
      category: categories.media,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: '<iframe src="https://www.openstreetmap.org/export/embed.html?bbox=-74.26%2C40.50%2C-73.70%2C40.92&layer=mapnik" style="width: 100%; height: 315px; border: none; border-radius: 8px;" allowfullscreen></iframe>'
    }
  ]

  // Component Blocks (Enhanced)
  const componentBlocks = [
    {
      id: 'react-hero-section',
      label: 'Hero Section',
      category: categories.react,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>`,
      content: {
        type: 'react-hero-section'
      }
    },
    {
      id: 'react-feature-card',
      label: 'Feature Card',
      category: categories.react,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: `
        <div style="padding: 30px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); background: white; text-align: center;">
          <div style="width: 60px; height: 60px; background: #667eea; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px;">🚀</div>
          <h3 style="margin-bottom: 15px; color: #333;">Amazing Feature</h3>
          <p style="color: #666; line-height: 1.6;">This is a description of the amazing feature that will help users understand its value.</p>
        </div>
      `
    },
    {
      id: 'react-testimonial',
      label: 'Testimonial',
      category: categories.react,
      media: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-gray-500">
        <path fill="currentColor" d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
      </svg>`,
      content: `
        <div style="padding: 30px; background: #f8f9fa; border-radius: 8px; text-align: center;">
          <div style="width: 80px; height: 80px; background: #667eea; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; color: white; font-size: 32px;">👤</div>
          <p style="font-style: italic; margin-bottom: 20px; color: #555; line-height: 1.6;">"This platform has completely transformed how we build websites. It's intuitive, powerful, and saves us hours of development time."</p>
          <h4 style="margin-bottom: 5px; color: #333;">John Doe</h4>
          <p style="color: #667eea; font-size: 0.9rem;">CEO, Tech Company</p>
        </div>
      `
    },
    {
      id: 'react-cta-section',
      label: 'Call to Action',
      category: categories.react,
      content: {
        type: 'react-cta-section'
      }
    },
    {
      id: 'react-pricing-card',
      label: 'Pricing Card',
      category: categories.react,
      content: {
        type: 'react-pricing-card'
      }
    },
    {
      id: 'react-contact-form',
      label: 'Contact Form',
      category: categories.react,
      content: `
        <style>
          .react-contact-form .form-success, .react-contact-form .form-error { display: none; text-align: center; margin-top: 12px; font-weight: 500; }
          .react-contact-form .form-success.visible, .react-contact-form .form-error.visible { display: block; }
          .react-contact-form .form-success { color: #667eea; }
          .react-contact-form .form-error { color: #d93025; font-size: 0.9rem; }
        </style>
        <div class="react-contact-form" style="max-width: 500px; margin: 0 auto; padding: 40px; background: white; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
          <h2 style="text-align: center; margin-bottom: 30px; color: #333;">Contact Us</h2>
          <form class="agent-form">
            <div style="margin-bottom: 20px;">
              <label style="display: block; margin-bottom: 5px; color: #555;">Name</label>
              <input type="text" name="name" style="width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem;" placeholder="Your name" required>
            </div>
            <div style="margin-bottom: 20px;">
              <label style="display: block; margin-bottom: 5px; color: #555;">Email</label>
              <input type="email" name="email" style="width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem;" placeholder="your@email.com" required>
            </div>
            <div style="margin-bottom: 20px;">
              <label style="display: block; margin-bottom: 5px; color: #555;">Message</label>
              <textarea name="message" style="width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem; height: 120px; resize: vertical;" placeholder="Your message" required></textarea>
            </div>
            <button type="submit" class="contact-btn" style="background: #667eea; color: white; padding: 12px 30px; border: none; border-radius: 25px; font-size: 1.1rem; cursor: pointer; width: 100%;">Send Message</button>
            <div class="form-success">Thanks! We'll be in touch soon.</div>
            <div class="form-error">Something went wrong -- please try again or contact us directly.</div>
          </form>
        </div>
      `
    },
    {
      id: 'react-navigation',
      label: 'Navigation',
      category: categories.react,
      content: `
        <nav style="background: white; padding: 15px 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; max-width: 1200px; margin: 0 auto;">
            <div style="font-size: 1.5rem; font-weight: bold; color: #333;">Logo</div>
            <ul style="display: flex; list-style: none; margin: 0; padding: 0; gap: 30px;">
              <li><a href="#" style="color: #333; text-decoration: none; font-weight: 500;">Home</a></li>
              <li><a href="#" style="color: #333; text-decoration: none; font-weight: 500;">About</a></li>
              <li><a href="#" style="color: #333; text-decoration: none; font-weight: 500;">Services</a></li>
              <li><a href="#" style="color: #333; text-decoration: none; font-weight: 500;">Contact</a></li>
            </ul>
          </div>
        </nav>
      `
    },
    {
      id: 'react-footer',
      label: 'Footer',
      category: categories.react,
      content: `
        <footer style="background: #333; color: white; padding: 40px 20px;">
          <div style="max-width: 1200px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 30px;">
              <div>
                <h3 style="margin-bottom: 15px;">Company</h3>
                <p style="line-height: 1.6; opacity: 0.8;">Building amazing websites with modern technology and user-friendly interfaces.</p>
              </div>
              <div>
                <h3 style="margin-bottom: 15px;">Quick Links</h3>
                <ul style="list-style: none; padding: 0; line-height: 1.8;">
                  <li><a href="#" style="color: white; text-decoration: none; opacity: 0.8;">Home</a></li>
                  <li><a href="#" style="color: white; text-decoration: none; opacity: 0.8;">About</a></li>
                  <li><a href="#" style="color: white; text-decoration: none; opacity: 0.8;">Services</a></li>
                  <li><a href="#" style="color: white; text-decoration: none; opacity: 0.8;">Contact</a></li>
                </ul>
              </div>
              <div>
                <h3 style="margin-bottom: 15px;">Contact</h3>
                <p style="line-height: 1.6; opacity: 0.8;">Email: info@company.com<br>Phone: (555) 123-4567</p>
              </div>
            </div>
            <div style="border-top: 1px solid #555; margin-top: 30px; padding-top: 20px; text-align: center; opacity: 0.8;">
              © 2024 Company Name. All rights reserved.
            </div>
          </div>
        </footer>
      `
    }
  ]

  // Real Estate Blocks
  const realEstateBlocks = [
    {
      id: 'real-estate-hero-with-search',
      label: 'Hero With Search',
      category: categories.heroSections,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
      </svg>`,
      content: {
        type: 'hero-with-search'
      }
    },
    {
      id: 'real-estate-property-card',
      label: 'Property Card',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: `
        <div class="gdx-properties" data-type="grid" data-columns="1" data-limit="1"></div>
      `
    },
    {
      id: 'real-estate-property-search',
      label: 'Property Search',
      category: categories.searchFilters,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
      </svg>`,
      content: {
        type: 'real-estate-property-search'
      }
    },
    {
      id: 'real-estate-agent-profile',
      label: 'Agent Profile',
      category: categories.aboutTeam,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>`,
      content: {
        type: 'real-estate-agent-profile'
      }
    },
    {
      id: 'real-estate-testimonials-section',
      label: 'Testimonials Section',
      category: categories.aboutTeam,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
      </svg>`,
      content: {
        type: 'real-estate-testimonials-section'
      }
    },
    {
      id: 'real-estate-featured-properties-carousel',
      label: 'Featured Properties Carousel',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z"/>
      </svg>`,
      content: `
        <style>
          .featured-properties-carousel { padding: 40px 0 24px 0; background: #f8f9fa; }
          .carousel-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        </style>
        <section class="featured-properties-carousel">
          <h2 class="carousel-title">Featured Properties</h2>
          <div class="gdx-properties" data-type="slider" data-limit="6"></div>
        </section>
      `
    },
    {
      id: 'real-estate-map-with-pins',
      label: 'Map With Property Pins',
      category: categories.maps,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>`,
      content: {
        type: 'real-estate-map-with-pins'
      }
    },
    {
      id: 'real-estate-contact-agent-form',
      label: 'Contact Agent Form',
      category: categories.formsContact,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
      </svg>`,
      content: `
        <style>
          .contact-agent-form { padding: 40px 0 24px 0; background: #f8f9fa; }
          .contact-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .agent-form { max-width: 420px; margin: 0 auto; display: flex; flex-direction: column; gap: 18px; background: #fff; border-radius: 18px; box-shadow: 0 4px 24px rgba(0,0,0,0.10); padding: 32px 24px; }
          .agent-form input, .agent-form textarea { font-size: 1rem; padding: 14px 16px; border: 1px solid #e5e5e7; border-radius: 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f9f9fa; color: #1d1d1f; outline: none; transition: border 0.2s; }
          .agent-form input:focus, .agent-form textarea:focus { border: 1.5px solid #007AFF; }
          .agent-form textarea { min-height: 90px; resize: vertical; }
          .contact-btn { background: #007AFF; color: #fff; border: none; border-radius: 10px; padding: 14px 0; font-size: 1rem; font-weight: 600; cursor: pointer; transition: background 0.2s; }
          .contact-btn:hover { background: #0056CC; }
          .contact-btn:disabled { opacity: 0.6; cursor: default; }
          .form-success, .form-error { display: none; text-align: center; margin-top: 12px; font-weight: 500; }
          .form-success.visible, .form-error.visible { display: block; }
          .form-success { color: #007AFF; }
          .form-error { color: #d93025; font-size: 0.9rem; }
          @media (max-width: 600px) { .agent-form { padding: 18px 6px; } }
        </style>
        <section class="contact-agent-form">
          <h2 class="contact-title">Contact an Agent</h2>
          <form class="agent-form">
            <input type="text" name="name" placeholder="Your Name" required />
            <input type="email" name="email" placeholder="Email Address" required />
            <input type="tel" name="phone" placeholder="Phone Number" />
            <textarea name="message" placeholder="How can we help you?" required></textarea>
            <button type="submit" class="contact-btn">Request Info</button>
            <div class="form-success">Thanks! We'll be in touch soon.</div>
            <div class="form-error">Something went wrong -- please try again or contact us directly.</div>
          </form>
        </section>
        <script>
          (function () {
            document.querySelectorAll('.agent-form').forEach(function (form) {
              if (form.dataset.gdxWired) return;
              form.dataset.gdxWired = 'true';
              form.addEventListener('submit', function (e) {
                e.preventDefault();
                var btn = form.querySelector('.contact-btn');
                var successEl = form.querySelector('.form-success');
                var errorEl = form.querySelector('.form-error');
                if (successEl) successEl.classList.remove('visible');
                if (errorEl) errorEl.classList.remove('visible');

                var tracker = window.TenantTracker;
                if (!tracker || !tracker.identify) {
                  if (errorEl) errorEl.classList.add('visible');
                  return;
                }

                var fullName = ((form.querySelector('[name="name"]') || {}).value || '').trim();
                var nameParts = fullName.split(/\\s+/);
                var firstName = nameParts.shift() || '';
                var lastName = nameParts.join(' ');

                if (btn) btn.disabled = true;
                tracker.identify({
                  email: (form.querySelector('[name="email"]') || {}).value || '',
                  firstName: firstName,
                  lastName: lastName,
                  phone: (form.querySelector('[name="phone"]') || {}).value || '',
                  customFields: { message: (form.querySelector('[name="message"]') || {}).value || '' }
                }).then(function (ok) {
                  if (btn) btn.disabled = false;
                  if (ok) {
                    form.reset();
                    if (successEl) successEl.classList.add('visible');
                  } else if (errorEl) {
                    errorEl.classList.add('visible');
                  }
                });
              });
            });
          })();
        </script>
      `
    },
    {
      id: 'real-estate-brokerage-about-section',
      label: 'Brokerage/About Section',
      category: categories.aboutTeam,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>`,
      content: `
        <style>
          .brokerage-about-section { padding: 40px 0 24px 0; background: #f8f9fa; }
          .about-header { display: flex; align-items: center; gap: 32px; max-width: 900px; margin: 0 auto 32px auto; flex-wrap: wrap; }
          .agency-logo { width: 80px; height: 80px; object-fit: contain; border-radius: 16px; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
          .about-mission h2 { font-size: 2rem; font-weight: 700; margin-bottom: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .about-mission p { font-size: 1.1rem; color: #6e6e73; margin: 0; }
          .about-team { max-width: 900px; margin: 0 auto 32px auto; }
          .about-team h3 { font-size: 1.25rem; font-weight: 600; margin-bottom: 18px; }
          .team-list { display: flex; gap: 32px; flex-wrap: wrap; }
          .team-member { background: #fff; border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 18px 16px; text-align: center; min-width: 160px; flex: 1 1 160px; }
          .team-photo { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; margin-bottom: 10px; }
          .team-name { font-weight: 600; font-size: 1.05rem; }
          .team-role { color: #6e6e73; font-size: 0.95rem; }
          .about-awards { max-width: 900px; margin: 0 auto; }
          .about-awards h3 { font-size: 1.25rem; font-weight: 600; margin-bottom: 18px; }
          .awards-list { display: flex; gap: 32px; flex-wrap: wrap; }
          .award { background: #fff; border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 18px 24px; text-align: center; min-width: 120px; flex: 1 1 120px; }
          .award-value { font-size: 1.5rem; font-weight: 700; color: #007AFF; }
          .award-label { color: #6e6e73; font-size: 1rem; }
          @media (max-width: 700px) { .about-header, .team-list, .awards-list { flex-direction: column; align-items: center; gap: 18px; } .about-header { text-align: center; } }
        </style>
        <section class="brokerage-about-section">
          <div class="about-header">
            <img src="https://via.placeholder.com/160x80.png?text=Your+Logo" alt="Agency Logo" class="agency-logo" />
            <div class="about-mission">
              <h2>About DreamX Realty</h2>
              <p>We are committed to redefining real estate with integrity, innovation, and a client-first approach. Our award-winning team delivers exceptional results and a seamless experience, every time.</p>
            </div>
          </div>
          <div class="about-team">
            <h3>Meet Our Team</h3>
            <div class="team-list">
              <div class="team-member">
                <img src="https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=200&q=80&auto=format&fit=facearea&facepad=2.5" alt="Sarah Lee" class="team-photo" />
                <div class="team-name">Sarah Lee</div>
                <div class="team-role">Principal Broker</div>
              </div>
              <div class="team-member">
                <img src="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=200&q=80&auto=format&fit=facearea&facepad=2.5" alt="James Kim" class="team-photo" />
                <div class="team-name">James Kim</div>
                <div class="team-role">Agent</div>
              </div>
              <div class="team-member">
                <img src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=200&q=80&auto=format&fit=facearea&facepad=2.5" alt="Maria Garcia" class="team-photo" />
                <div class="team-name">Maria Garcia</div>
                <div class="team-role">Agent</div>
              </div>
            </div>
          </div>
          <div class="about-awards">
            <h3>Our Achievements</h3>
            <div class="awards-list">
              <div class="award">
                <div class="award-value">15+</div>
                <div class="award-label">Years in Business</div>
              </div>
              <div class="award">
                <div class="award-value">500+</div>
                <div class="award-label">Homes Sold</div>
              </div>
              <div class="award">
                <div class="award-value">300+</div>
                <div class="award-label">5-Star Reviews</div>
              </div>
            </div>
          </div>
        </section>
      `
    },
    {
      id: 'real-estate-map-search-with-filters',
      label: 'Map Search with Filters',
      category: categories.searchFilters,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
      </svg>`,
      content: {
        type: 'real-estate-map-search-with-filters'
      }
    },
    {
      id: 'real-estate-property-gallery',
      label: 'Property Gallery',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
      </svg>`,
      content: `
        <style>
          .property-gallery { padding: 40px 0 24px 0; background: #f8f9fa; }
          .gallery-title { font-size: 2rem; font-weight: 700; margin-bottom: 24px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .gallery-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 18px; max-width: 900px; margin: 0 auto; }
          .gallery-thumb { width: 100%; height: 120px; object-fit: cover; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); cursor: pointer; transition: transform 0.15s; }
          .gallery-thumb:hover { transform: scale(1.04); }
        </style>
        <section class="property-gallery">
          <h2 class="gallery-title">Property Gallery</h2>
          <div class="gallery-grid">
            <img src="https://images.unsplash.com/photo-1460518451285-97b6aa326961?w=800&q=80&auto=format&fit=crop" class="gallery-thumb" />
            <img src="https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&q=80&auto=format&fit=crop" class="gallery-thumb" />
            <img src="https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?w=800&q=80&auto=format&fit=crop" class="gallery-thumb" />
            <img src="https://images.unsplash.com/photo-1523217582562-09d0def993a6?w=800&q=80&auto=format&fit=crop" class="gallery-thumb" />
            <img src="https://images.unsplash.com/photo-1465101046530-73398c7f28ca?w=800&q=80&auto=format&fit=crop" class="gallery-thumb" />
          </div>
        </section>
      `
    },
    {
      id: 'real-estate-property-comparison-table',
      label: 'Property Comparison Table',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
      </svg>`,
      content: {
        type: 'real-estate-property-comparison-table'
      }
    },
    {
      id: 'real-estate-contract-offer-simulator',
      label: 'Contract Offer Simulator',
      category: categories.formsContact,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
      </svg>`,
      content: {
        type: 'real-estate-contract-offer-simulator'
      }
    },
    {
      id: 'real-estate-property-detail-page',
      label: 'Property Detail Page',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: {
        type: 'real-estate-property-detail-page'
      }
    },
    {
      id: 'real-estate-listing-detail-dynamic',
      label: 'Listing Detail (Dynamic)',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>`,
      content: `
        <style>
          .gdx-listing-detail { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1d1d1f; background: #f5f5f7; }
          .gdx-detail-photo { width: 100%; height: 60vh; min-height: 420px; background: #d9d9de; display: flex; align-items: center; justify-content: center; color: #6e6e73; font-size: 1.1rem; letter-spacing: 0.04em; text-transform: uppercase; overflow: hidden; }
          .gdx-detail-photo img { width: 100%; height: 100%; object-fit: cover; }
          .gdx-detail-header { max-width: 1000px; margin: 0 auto; padding: 40px 20px 0; }
          .gdx-detail-price { display: inline-block; background: #007aff; color: white; padding: 10px 22px; border-radius: 25px; font-size: 1.2rem; font-weight: 600; margin-bottom: 12px; }
          .gdx-detail-title { font-size: 2.4rem; font-weight: 700; margin: 0 0 6px 0; }
          .gdx-detail-address { font-size: 1.1rem; color: #6e6e73; margin: 0 0 24px 0; }
          .gdx-detail-stats { max-width: 1000px; margin: 0 auto; padding: 0 20px 40px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
          .gdx-stat { background: white; border-radius: 12px; padding: 20px; text-align: center; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
          .gdx-stat-number { display: block; font-size: 1.6rem; font-weight: 700; color: #007aff; margin-bottom: 4px; }
          .gdx-stat-label { font-size: 0.85rem; color: #86868b; text-transform: uppercase; letter-spacing: 0.04em; }
          .gdx-detail-meta { max-width: 1000px; margin: 0 auto; padding: 0 20px 60px; color: #6e6e73; }
          @media (max-width: 768px) { .gdx-detail-stats { grid-template-columns: repeat(2, 1fr); } }
        </style>
        <div class="gdx-listing-detail">
          <div class="gdx-detail-photo" id="gdx-detail-photo">Property Photo</div>
          <div class="gdx-detail-header">
            <div class="gdx-detail-price" id="gdx-detail-price">Price on request</div>
            <h1 class="gdx-detail-title" id="gdx-detail-title">Listing details</h1>
            <p class="gdx-detail-address" id="gdx-detail-address"></p>
          </div>
          <div class="gdx-detail-stats">
            <div class="gdx-stat"><span class="gdx-stat-number" id="gdx-detail-beds">—</span><span class="gdx-stat-label">Beds</span></div>
            <div class="gdx-stat"><span class="gdx-stat-number" id="gdx-detail-baths">—</span><span class="gdx-stat-label">Baths</span></div>
            <div class="gdx-stat"><span class="gdx-stat-number" id="gdx-detail-sqft">—</span><span class="gdx-stat-label">Sq Ft</span></div>
            <div class="gdx-stat"><span class="gdx-stat-number" id="gdx-detail-type">—</span><span class="gdx-stat-label">Type</span></div>
          </div>
          <div class="gdx-detail-meta">
            <span id="gdx-detail-listedby"></span>
          </div>
        </div>
      `
    },
    {
      id: 'real-estate-property-listing-page',
      label: 'Property Listing Page',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
      </svg>`,
      content: {
        type: 'real-estate-property-listing-page'
      }
    },
    {
      id: 'real-estate-property-comparison-page',
      label: 'Property Comparison Page',
      category: categories.propertyDisplays,
      media: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" style="display:block;margin:auto;color:#007AFF" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M9 3l-1.17 1.17L10.83 6H5v2h5.83L7.83 9.83 9 11l4-4-4-4zM21 19h-5.83l1.17-1.17L15 15l-4 4 4 4 1.17-1.17L15.17 20H21v-2z"/>
      </svg>`,
      content: {
        type: 'real-estate-property-comparison-page'
      }
    }
  ];

  // Combine all blocks
  const allBlocks = [
    ...layoutBlocks,
    ...basicBlocks,
    ...typographyBlocks,
    ...mediaBlocks,
    ...componentBlocks,
    ...realEstateBlocks
  ]

  // Register all blocks
  allBlocks.forEach(block => {
    const blockConfig = {
      label: block.label,
      category: block.category,
      content: block.content
    }
    
    // Only add media if it exists, don't override with star icon
    if (block.media) {
      blockConfig.media = block.media
    }
    
    BlockManager.add(block.id, blockConfig)
  })

  console.log('✅ All React blocks registered successfully')
}

// Register Mapbox components as DOM components
export function registerMapboxComponents(editor) {
  console.log('🗺️ Registering Mapbox components...')
  
  const DomComponents = editor.DomComponents
  
  // Register MapboxWithPins component
  DomComponents.addType('mapbox-with-pins', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: {
          'data-mapbox-component': 'MapboxWithPins',
          class: 'mapbox-component-wrapper'
        },
        components: `
          <div class="mapbox-with-pins">
            <div class="mapbox-header">
              <h2 class="mapbox-title">Explore Properties on the Map</h2>
              <p class="mapbox-subtitle">Interactive map powered by Mapbox</p>
            </div>
            <div class="mapbox-container" style="height: 500px;">
              <div class="mapbox-interactive-wrapper" data-react-component="MapboxWithPins">
                <div class="mapbox-loading">
                  <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f7fafc; border: 2px dashed #cbd5e0; border-radius: 8px; color: #4a5568; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
                    <div style="text-align: center;">
                      <svg style="width: 48px; height: 48px; margin: 0 auto 1rem auto; color: #cbd5e0;" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="10" r="3"/>
                        <path d="m12 21.7-.39-.39A11.96 11.96 0 0 1 2 10.6C2 5.91 5.91 2 10.6 2s8.6 3.91 8.6 8.6a11.96 11.96 0 0 1-9.61 11.1z"/>
                      </svg>
                      <p style="margin: 0; font-size: 14px;">Loading Interactive Map...</p>
                      <p style="margin: 0.5rem 0 0 0; font-size: 12px; color: #718096;">Mapbox component will render here</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        script: function() {
          // This will be called when the component is added to the canvas
          const wrapper = this.querySelector('[data-react-component="MapboxWithPins"]');
          if (wrapper) {
            // Add a click handler to show that this is interactive
            wrapper.addEventListener('click', function() {
              alert('Mapbox component clicked! This would render the actual interactive map on the published site.');
            });
            
            // Add some visual feedback
            wrapper.style.cursor = 'pointer';
            wrapper.addEventListener('mouseenter', function() {
              this.style.opacity = '0.8';
            });
            wrapper.addEventListener('mouseleave', function() {
              this.style.opacity = '1';
            });
          }
        }
      }
    }
  })
  
  // Register MapboxSearchWithFilters component
  DomComponents.addType('mapbox-search-filters', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: {
          'data-mapbox-component': 'MapboxSearchWithFilters',
          class: 'mapbox-search-component-wrapper'
        },
        components: `
          <div class="mapbox-search-with-filters">
            <div class="mapbox-search-header">
              <h2 class="mapbox-search-title">Property Map Search</h2>
              <p class="mapbox-search-subtitle">Find your perfect property with advanced search and filtering</p>
            </div>
            <div class="mapbox-filters-container">
              <div class="mapbox-filters-grid">
                <div class="mapbox-filter-group">
                  <label class="mapbox-filter-label">Search Location</label>
                  <input type="text" placeholder="Enter address, city, or neighborhood" class="mapbox-filter-input" />
                </div>
                <div class="mapbox-filter-group">
                  <label class="mapbox-filter-label">Property Type</label>
                  <select class="mapbox-filter-select">
                    <option>All Types</option>
                    <option>House</option>
                    <option>Apartment</option>
                    <option>Condo</option>
                  </select>
                </div>
                <div class="mapbox-filter-group">
                  <label class="mapbox-filter-label">Min Price</label>
                  <input type="number" placeholder="Min $" class="mapbox-filter-input" />
                </div>
                <div class="mapbox-filter-group">
                  <label class="mapbox-filter-label">Max Price</label>
                  <input type="number" placeholder="Max $" class="mapbox-filter-input" />
                </div>
              </div>
            </div>
            <div class="mapbox-search-container" style="height: 600px;">
              <div class="mapbox-interactive-wrapper" data-react-component="MapboxSearchWithFilters">
                <div class="mapbox-loading">
                  <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f7fafc; border: 2px dashed #cbd5e0; border-radius: 8px; color: #4a5568; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
                    <div style="text-align: center;">
                      <svg style="width: 48px; height: 48px; margin: 0 auto 1rem auto; color: #cbd5e0;" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                        <circle cx="11" cy="8" r="2"/>
                      </svg>
                      <p style="margin: 0; font-size: 14px;">Loading Search Map...</p>
                      <p style="margin: 0.5rem 0 0 0; font-size: 12px; color: #718096;">Interactive search map will render here</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        script: function() {
          const wrapper = this.querySelector('[data-react-component="MapboxSearchWithFilters"]');
          if (wrapper) {
            wrapper.addEventListener('click', function() {
              alert('Mapbox Search component clicked! This would render the actual interactive search map on the published site.');
            });
            
            wrapper.style.cursor = 'pointer';
            wrapper.addEventListener('mouseenter', function() {
              this.style.opacity = '0.8';
            });
            wrapper.addEventListener('mouseleave', function() {
              this.style.opacity = '1';
            });
          }
        }
      }
    }
  })
  
  // Register MapboxWithDraw component
  DomComponents.addType('mapbox-with-draw', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: {
          'data-mapbox-component': 'MapboxWithDraw',
          class: 'mapbox-draw-component-wrapper'
        },
        components: `
          <div class="mapbox-with-draw">
            <div class="mapbox-header">
              <h2 class="mapbox-title">Interactive Drawing Map</h2>
              <p class="mapbox-subtitle">Draw shapes, lines, and points on the map</p>
            </div>
            <div class="mapbox-controls-bar">
              <div class="mapbox-draw-tools">
                <button class="mapbox-tool-button">📍 Point</button>
                <button class="mapbox-tool-button">📏 Line</button>
                <button class="mapbox-tool-button">⬟ Polygon</button>
                <button class="mapbox-tool-button">👆 Select</button>
              </div>
              <div class="mapbox-action-tools">
                <button class="mapbox-action-button">📊 Data (0)</button>
                <button class="mapbox-action-button">🗑️ Clear</button>
                <button class="mapbox-action-button">💾 Export</button>
                <button class="mapbox-action-button">📁 Import</button>
              </div>
            </div>
            <div class="mapbox-container" style="height: 500px;">
              <div class="mapbox-interactive-wrapper" data-react-component="MapboxWithDraw">
                <div class="mapbox-loading">
                  <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f7fafc; border: 2px dashed #cbd5e0; border-radius: 8px; color: #4a5568; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
                    <div style="text-align: center;">
                      <svg style="width: 48px; height: 48px; margin: 0 auto 1rem auto; color: #cbd5e0;" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11H1v-1h8v1Z"/>
                        <path d="M22 11h-8v-1h8v1Z"/>
                        <path d="M14 19a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"/>
                        <path d="M3 12a9 9 0 1 0 18 0 9 9 0 1 0-18 0"/>
                        <path d="M21 3l-3 3"/>
                        <path d="M18 6l3-3"/>
                      </svg>
                      <p style="margin: 0; font-size: 14px;">Loading Drawing Map...</p>
                      <p style="margin: 0.5rem 0 0 0; font-size: 12px; color: #718096;">Interactive drawing map will render here</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mapbox-footer">
              <div class="mapbox-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Click the drawing tools to create points, lines, or polygons</li>
                  <li>Use the Select tool to edit or delete features</li>
                  <li>Export your drawings as GeoJSON or import existing files</li>
                </ul>
              </div>
            </div>
          </div>
        `,
        script: function() {
          const wrapper = this.querySelector('[data-react-component="MapboxWithDraw"]');
          if (wrapper) {
            wrapper.addEventListener('click', function() {
              alert('Mapbox Draw component clicked! This would render the actual interactive drawing map on the published site.');
            });
            
            wrapper.style.cursor = 'pointer';
            wrapper.addEventListener('mouseenter', function() {
              this.style.opacity = '0.8';
            });
            wrapper.addEventListener('mouseleave', function() {
              this.style.opacity = '1';
            });
          }
        }
      }
    }
  })
  
  console.log('✅ Mapbox components registered successfully')
} 